// Roulette — PORTRAIT, front-end only. No engine, no wallet, no settlement:
// the result is drawn locally and nothing is recorded anywhere.
// Research + decisions: docs/ROULETTE_PLAN.md
//
// ─── THE LAYOUT CONTRACT ──────────────────────────────────────────────────
// This page renders inside `.force-portrait`, which is `position: fixed`,
// `100dvh` and `overflow: hidden`. That is a HARD CEILING, not a scroll origin.
// The first build laid itself out as an ordinary document, came to 916px on a
// 844px screen, and had 72px — including the SPIN button — clipped away with no
// gesture that could reach it. So: five bands, and only ONE of them is elastic.
//
//   A top bar     fixed 64   copied from Ludo, only the gradient differs
//   B peephole    fixed 64   a SLIT onto a full-size wheel
//   D felt        flex-1     the WHOLE board, always, never scrolls
//   E controls    fixed 146  evens rail · chips · actions + SPIN
//
// NOTHING SCROLLS. The board sizes its 14 rows to whatever height is left, so
// every number is on screen on every device. The previous build let the felt
// scroll: on a real phone that gesture fights the drag (you cannot tell "drag a
// chip" from "scroll the list" on the same axis) and it hid numbers that were
// visible before. Rows are a little shorter; everything is reachable.
//
// THE PEEPHOLE. The wheel is ALWAYS rendered at full size. What changes is the
// window onto it: 64px at rest — a slim slit showing the middle of the wheel —
// opening to the full wheel when the round starts, then closing again. The
// window is absolutely positioned, so opening it costs ZERO layout: the felt
// never moves, and the wheel never rescales (no re-render, no blurry tween).
//
// ─── THE SPIN SEQUENCE (owner's spec, unchanged) ──────────────────────────
//   tap SPIN -> betting locks + wheel grows, NOTHING rotating
//   -> ~1s hold -> THEN it rolls -> orbit, drop, bounce, settle
//   -> the winner holds on the board for 3.6s -> next round.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import '../ludo/ludo.css';
import './roulette.css';
import './social.css';
import Wheel, { ResultInset } from './Wheel';
import Board, { RAIL_FRAC, GAP } from './Board';
import Overlay, { type Highlight, type HlReason, type PlacedChip } from './Overlay';
import {
  betsAtCell, cellFrac, cellsForNumbers, layoutBox, linePoints, makeBet, measureLattice, numAt,
  toWrapperLocal,
  pitchX, pitchY, returnOf, targetAtCell, winningZones,
  type Lattice, type PendingBet, type Target,
} from './lattice';
import { press } from './press';
import {
  CallBanner, Clock, CountIn, FakeChipLayer, Leaderboard, RecordRail, ResultCall,
} from './Social';
import { applyRoundWins, pickTableTarget, rollRoster, rotateRoster, type Seat } from './social';
import {
  initSound, setSoundEnabled, unlock as unlockSound, unlockSpeech, speechStatus,
  initVoice, isVoiceOn, setVoiceOn, sVoiceOn,
  sAimTick, sBallDrop, sBallRoll, sCallNumber, sChipOff, sChipPick, sChipPlace,
  sCountPip, sDenied, sLose, sSettle, sSpinStart, sTap, sWin, primeVoices,
} from './sound';
import {
  bindMusicVisibility, currentTrack, cycleMusic, duckMusic, initMusic, intensityFor,
  isMusicOn, setMusicIntensity, startMusic, stopMusic,
} from './music';
import { colourOf, PAYOUT, RETURN_X, planSpin, randomNumber, settle, type Bet, type BetKind, type Spin } from './engine';
import { PayoutPeek } from './PayoutPeek';
import { api } from '@baaligames/shared-ui/lib/api';
import { setWalletBalance } from '@baaligames/shared-ui/lib/wallet';
import { recentResults, recordResult, windowMsFor, WINDOW_MS, type Table } from './tables';
import { RouletteMenu } from './Menu';
/* ── SHARED MODE (Phase E, plan §5.7, 2026-09-06) ────────────────────────
   TYPE-ONLY for the clock, so a build with `roulette.shared_tables` off does
   not pull `sharedClock.ts` (and, through it, the poller and the Cristian
   sync) into this module at all: `Entry.tsx` imports it dynamically and hands
   the instance down. `sharedTiming` IS imported for real — it is pure
   arithmetic with no I/O, and it is what the shared branches below are
   written in terms of. Every one of those branches is guarded on `shared`
   being non-null, which it never is on the local path. */
import type { RouletteSharedClock, HistoryRow } from './sharedClock';
import { flightSecondsFor, LOCK_CUSHION_MS, type ClientPhase } from './sharedTiming';

const CHIPS = [10, 50, 100, 500, 1000, 5000];
/* `mode` is GONE from this payload as of Phase E (plan §5.7: "The legacy /spin
   `mode` field is dropped"). `roulette_rounds.mode` is still written for audit;
   it simply no longer travels to a client that never read it. */
type SpinResponse = {
  roundId: number; n: number;
  staked: number; returned: number; net: number; newBalance: number;
  perBet: { kind: BetKind; numbers: number[]; amount: number; payout: number }[];
};

/* REAL MONEY. The balance boots from GET /roulette/state and every settle
 * writes the server's newBalance. The old DEMO_START practice wallet is gone
 * — this game now plays on the same signed rupees as every other game. */

const ARM_MS = 1400;          // 420ms grow + ~1s hold, then it rolls
/* ─── the result phase, in order ──────────────────────────────────────────
 * Image 6 proves the beats are SEQUENTIAL, not simultaneous: the inset is still
 * on screen and the Record already updated while the board's win highlight has
 * already gone. t = 0 is the ball seating.
 *
 *      0   seat, settle, credit, Record pushes
 *   1200   camera pans DOWN; the result inset slides in from the left
 *   1460   every winning zone washes gold
 *   4460   zones clear      <- the hold is 3000ms, which the spec requires
 *   4700   "YOU HAVE WON" + coins launch from the winning chips
 *   4790   the wallet starts counting up
 *   7000   inset out, board cleared, next round armed
 */
const RESULT_HOLD_MS = 1200;   // seat -> pan down
const ZONES_IN_MS = 1460;
const ZONES_OUT_MS = 4460;     // ZONES_OUT_MS - ZONES_IN_MS >= 3000
const WIN_TEXT_MS = 4700;
const COUNT_UP_MS = 4790;
const ROUND_END_MS = 7000;
const COUNT_DUR_MS = 900;

/** The betting window. The reference runs a 20-second timer and spins whether
 *  or not anyone bet — the table does not wait for you. */
/* WINDOW_MS lives in tables.ts now — imported at the top with the rest, so the
   lobby's advertised pace and the table's real clock cannot drift apart. */

/* ─── the two message beats ───────────────────────────────────────────────
 * The banner is a CALL, not a label. It says its piece and gets out of the way
 * so the player can see the board they are betting on.
 *
 *   INTRO_MS  "Place Your Bet" holds, THEN clears, and only then does the
 *             clock start — the countdown belongs to the betting, not to the
 *             announcement.
 *   CLOSE_MS  "Stop Betting" holds after the clock hits zero, so it is read
 *             here, where the player is looking, before the camera moves.
 */
const INTRO_MS = 1800;
const CLOSE_MS = 2000;
const CONFIRM_MS = 1400;      // R7: how long a placed bet's coverage keeps flashing
// BOTH ways in, because they are both good and they were never what was broken:
//   TAP  a point  — the studs show you where the line bets are, tap one.
//   DRAG a token  — carry it from the tray, watch the loupe, drop it.
//
// What WAS broken: the number grid resolved taps from a CACHED copy of the
// grid's screen rect, refreshed by a ResizeObserver. A ResizeObserver fires on
// size changes and never on position changes — so on a real phone (toolbar
// collapse, 100dvh re-resolving, env() safe-area landing after first paint) the
// cached frame silently shifted and every number tap missed. The outside bets
// were fine throughout because they were resolved through the DOM. Both the
// drag build and the dots build died on the same cache; neither model was at
// fault. Everything below is now DOM-anchored, exactly like the rails.
const TAP_SLOP = 16;          // press-and-release within this = a tap
const DRAG_SLOP = 8;          // move further than this from a token = a carry
const HOLD_MS = 200;          // or just hold it
const LONGPRESS_MS = 450;     // long-press a cell -> "all bets here"
/* ─── the camera ──────────────────────────────────────────────────────────
 * Every number below is DERIVED at runtime from the measured canvas — none of
 * them is a hand-tuned pixel. The two shape constants:
 *
 *   D_FRAC    the wheel cabinet as a fraction of canvas width. The reference
 *             shot is 386/576 = 0.67; 0.65 with a 285 cap keeps the board's row
 *             pitch above 32px on a 390-wide phone. Every 2px of diameter costs
 *             1px of board height, because exactly half the wheel is off-screen
 *             at rest — so this is the single most expensive constant here.
 *   HUB_FRAC  where the wheel's hub sits at rest, as a fraction of canvas
 *             height. The reference puts it 78px down a 1280px screen.
 */
/** Stable empty set, so the winIds memo never hands Board a fresh object. */
const EMPTY_IDS: Set<string> = new Set();

/* MEASURED off ref4.jpeg: the bowl spans x 38.5..504.5 ⇒ diameter 466 px on a
 * 576-wide frame = 0.809 of the width. The SVG draws the wood's outer edge at
 * R_OUTER/200 = 98% of its box, so the box must be 0.809/0.98.
 * The previous 0.65 made the wheel 21% too small — the most visible single
 * error on the screen. */
const D_FRAC = 0.8255;
const D_MIN = 258;
const D_MAX = 360;
/* WHERE THE HUB SITS AT REST.
 * The reference puts it at 85/1280 = 0.0664 H. We lift it to 0.01 H — owner's
 * call, 2026-08-14: "make the spinning wheel slightly more up to give our betting
 * board more room", and again on the redesign pass. Every pixel the hub rises is
 * a pixel the felt gains, and the board is aspect-locked at 3:7, so height is the
 * only thing that makes it wider. At -0.03 H the hub sits just above the top edge
 * and the bowl's lower ~40% is on screen at rest — enough to read as a wheel
 * waiting to spin, which is all it has to do while the player is betting. */
const HUB_FRAC = -0.03;
/* WHERE THE HUB SITS WHEN PANNED UP — unchanged, and the reason the pan is now
 * derived rather than a constant. The reference's spin framing has the hub at
 * 0.0664 + 0.1227 = 0.1891 H with the whole bowl on screen; keeping that fixed
 * while HUB_FRAC moves means the throw absorbs the difference and the SPIN view
 * stays pixel-identical to ref4. */
/* 2026-08-15, owner: "we are hiding some part of our spinner table where the
 * result is being announced... scroll it a little more up so that the top part
 * should not irritate."
 *
 * He is right, and the reason is that the reference framing this number came from
 * had NO pinned chrome at the top. Since then the wallet pill and the menu button
 * moved up there, and they occupy roughly 0.055 H — so the "whole bowl on screen"
 * that 0.1891 delivered is now the whole bowl MINUS whatever the wallet row
 * covers. Pushing the hub down the screen by another 0.037 H clears that row with
 * a little air to spare, and costs nothing: the pan is derived, so only the
 * panned-UP framing moves. The resting framing, which is what the betting board
 * is laid out against, is untouched. */
const HUB_UP_FRAC = 0.2261;
const PAN_MS = 520;          // must match the transition in roulette.css

// 'result' = the wheel is showing the number in the open peephole.
// 'reveal'  = the peephole has CLOSED and the BOARD is showing it. Betting stays
//             locked through both. Splitting them is what lets the wheel have
//             its moment and then hand over to the board, instead of the two
//             fighting for the same seconds.
/**
 * 'closing' is the beat between the clock hitting zero and the camera moving.
 * Betting is already shut — the money boundary is the clock, not the pan — but
 * the board is still on screen so "Stop Betting" can be read where the player
 * is looking. Without it the message and the pan happened in the same frame and
 * nobody could read either.
 */
type Phase = 'betting' | 'closing' | 'arming' | 'spinning' | 'result' | 'reveal';

/** A bet on the felt. fx/fy are the chip's position in PITCH units, so a resize
 *  or a rotation can never strand a chip away from the bet it belongs to. */
type Placed = Bet & { title: string; sub: string; fx: number | null; fy: number | null; centre: boolean };

/**
 * `table` is the room the player picked in the lobby (Entry.tsx). It is optional
 * so this component still stands alone — but when it is present the board opens
 * with that table's REAL past already in the Record column, which is the half of
 * the realism problem the lobby alone does not solve.
 */
export default function RoulettePage({ table, onChangeTable, shared = null }: {
  table?: Table; onChangeTable?: () => void;
  /**
   * SHARED MODE (Phase E, plan §5.7, 2026-09-06). Null on the local per-user path — which is
   * every build with `roulette.shared_tables` off, and therefore everything a real player has
   * reached since 1 September.
   *
   * When it is present this component keeps every pixel it had and changes exactly four
   * things: the round comes from the server's stamps instead of a local timer, chips are
   * posted per placement instead of as one slip at the close, the balance is the server's
   * after each ack, and the history is the table's real one. Everything else — the camera, the
   * lattice, the drag-to-bet, the music, the crowd — is untouched, and every branch below that
   * reads `shared` is guarded on it being non-null.
   */
  shared?: RouletteSharedClock | null;
} = {}) {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  /* False until /roulette/state answers. Placing and the low-balance rescue
     are gated on it: with a 0 pre-fetch balance the rescue would pop the
     moment a table is joined (the exact bug the audit's C10 fix warned the
     integration about). */
  const walletLoaded = useRef(false);
  /* The same fact as a RENDER input.
     `walletLoaded` is a ref, so nothing repaints when it flips — which is why the wallet pill
     showed a confident `0` for the whole of the boot fetch. Every other game in this family
     shows `₹—` until the server has spoken (andar_bahar/Page.tsx `walletReady()`), and it is
     the same argument here on a much older game: a balance nobody has confirmed is not a
     balance, and this one is real rupees. Set in lockstep with the ref, never on its own.
     (2026-09-06.) */
  const [walletReady, setWalletReady] = useState(false);
  /* Server betting bounds; the table's advertised minimum is a client fiction
     and is INTERSECTED with these, never trusted alone. */
  const srvBet = useRef<{ min: number; max: number; maxPerSpin: number } | null>(null);
  const [bets, setBets] = useState<Placed[]>([]);
  /* One entry per PLACEMENT, in tap order — which is what Undo pops. `key` is the shared
     path's idempotency key for that placement, i.e. the one `shared_round_bets` row it will
     become; undefined on the local path, where a placement is not a row until the close. */
  const [journal, setJournal] = useState<{ id: string; amount: number; key?: string }[]>([]);
  const [lastBets, setLastBets] = useState<Placed[]>([]);
  // NOTHING is pre-selected. Until a token is picked the board shows no points
  // and takes no bets. Pre-picking ₹100 quietly staked the house's guess on the
  // player's behalf, and hid the first decision the game should ask them to make.
  const [chip, setChip] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>('betting');
  const [spin, setSpin] = useState<Spin | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [winner, setWinner] = useState<number | null>(null);
  /* SEEDED FROM THE TABLE'S OWN CLOCK, not empty.
     The owner: "there are no older records whenever the user enters for the
     first time, which is making it highly suspicious." A table that has been
     running all day has a past; these are the results of the rounds that
     actually elapsed before the player walked in, derived in tables.ts from the
     wall clock, so they are already there on the first paint. */
  /* SHARED MODE STARTS EMPTY. §5.7: "in shared mode the fake history is deleted". The first
     `/state` fills this with the numbers the table REALLY produced, and a table with no
     settled rounds yet correctly shows none — seeding a derived strip here would put a
     fiction on screen for a frame, on a record two players can compare. (2026-09-06.) */
  const [history, setHistory] = useState<number[]>(
    () => (shared || !table ? [] : recentResults(table, Date.now(), 24)),
  );
  /** The room's pace, not a setting. Chosen in the lobby and fixed for the
   *  session — see the note where the old toggle used to be. */
  const quick = table?.fast ?? false;
  const [outcome, setOutcome] = useState<{ net: number; returned: number } | null>(null);
  const [idleFrom] = useState(() => performance.now());
  const [lattice, setLattice] = useState<Lattice | null>(null);
  // The wheel is a FIXED full size and never changes — only the window onto it
  // does. A number, not a percentage: an <svg> with percentage width/height
  // inside an auto-sized box has nothing to resolve against and falls back to
  // the CSS default object size (300x150).
  const [wheelPx, setWheelPx] = useState(254);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sheetMyBets, setSheetMyBets] = useState(false);
  /* The practice-chips top-up card. See the TopUp component for why the wallet
     button opens THIS in the standalone build instead of leaving the game. */
  const [topup, setTopup] = useState(false);
  const [sheetHelp, setSheetHelp] = useState(false);
  /** Every settled round this session — what was staked, what came, what it
   *  paid. The owner asked for this in image 6: "a button where user could see
   *  all its betting ... and whether it has won or not, how much". */
  const [ledger, setLedger] = useState<{ n: number; staked: number; returned: number; bets: number }[]>([]);
  /** What the Settings test row reports back — the chosen voice, or why none. */
  const [voiceInfo, setVoiceInfo] = useState('');
  /** Music: on/off plus WHICH tune. One tile cycles through both. */
  const [music, setMusic] = useState(() => ({ on: initMusic(), name: currentTrack().name }));
  /* Voice is its own switch now — it used to mirror the SOUND flag, which is why
     it could never be turned off independently. Defaults on, like the other two. */
  const [voiceOn, setVoiceOnState] = useState(initVoice);
  const [soundOn, setSoundOn] = useState(() => {
    // The browser's voice list loads asynchronously and getVoices() is empty
    // until it has. Asking for it here means the FIRST result of a session is
    // already called in the right voice rather than the default one.
    primeVoices();
    return initSound();
  });
  /* THE MUSIC BED. Starts on mount and stops on unmount, so it plays for exactly
     as long as a table is open — never in the lobby, never after Change table.
     Mount is already downstream of a real user gesture (PLAY NOW), which is what
     iOS requires before an AudioContext will make a sound at all. */
  useEffect(() => {
    if (isMusicOn()) startMusic();
    const unbind = bindMusicVisibility();
    return () => { unbind(); stopMusic(); };
  }, []);


  /** The three-second "what does each square pay" peek. See PayoutPeek.tsx. */
  const [peek, setPeek] = useState(false);

  const [hls, setHls] = useState<Partial<Record<HlReason, Highlight>>>({});
  /** The win / lose curtain, shown while the board holds the result. */
  const [banner, setBanner] = useState<{ won: boolean; amount: number } | null>(null);
  /** The winning zones' gold wash — its own state, because it is a TIMED beat
   *  that starts after the pan and ends before the payout, not simply "there is
   *  a winner". */
  const [zonesLit, setZonesLit] = useState(false);
  /** Money credited at settle but not yet shown in the wallet, so the count-up
   *  has somewhere to count FROM. Cleared when it finishes. */
  const [creditPending, setCreditPending] = useState(0);
  const walletRef = useRef<HTMLSpanElement | null>(null);

  /* ─── the social layer ────────────────────────────────────────────────
     All of this is SIMULATED and none of it touches the wallet, the bet list
     or the stake total. It is deliberately kept in its own state so the whole
     layer can be deleted without unpicking the money path. */
  const [railPx, setRailPx] = useState(0);
  /* A QUICK ROOM BETS FOR TWELVE SECONDS, a normal one for twenty. Both come
     from tables.ts so the pace the lobby advertises, the history it derived and
     the clock that actually runs here are one number rather than three. */
  /* In shared mode the pace is the SERVER's `shared_tables.window_ms`, because that is the
     number the round is actually run on — the lobby's `fast` flag is only how the room was
     labelled. They agree today (a `quick` row is seeded at 12 000 ms, the same as the local
     QUICK_WINDOW_MS), and reading the server's means they cannot stop agreeing. */
  const WINDOW = shared ? (shared.table()?.windowMs ?? (table ? windowMsFor(table) : WINDOW_MS))
    : (table ? windowMsFor(table) : WINDOW_MS);
  /* The room's own minimum -- the lobby card advertises it ("min Rs 50") and
     until now nothing enforced it, which makes the card read as decoration.
     (Audit m1/m9.) */
  const [srvMin, setSrvMin] = useState(0);
  /* Who the player IS, for the leaderboard's YOU card: first name (fallback
     username) + profile photo. One fetch, non-blocking, purely cosmetic. */
  const youRef = useRef<{ name: string; photo: string | null }>({ name: 'You', photo: null });
  /* This round's gross win, while the result is on screen — what the YOU card
     competes with the cast's gains on. */
  const [youWin, setYouWin] = useState(0);
  /* The room's advertised minimum INTERSECTED with the house's: the rooms are
     client fictions, so the server's floor always applies underneath them. */
  const MIN_BET = Math.max(table?.minBet ?? 10, srvMin);

  /* THE WALLET BOOT. One /state on mount: the real balance, the game's on/off
     switch, and the server's betting bounds. Everything money-facing waits on
     walletLoaded so a slow answer can never present a ₹0 wallet as truth. */
  useEffect(() => {
    let dead = false;
    /* SHARED MODE takes none of this. The clock is already polling `/state` (it was built
       during the join wait, precisely so the board opens on a landed answer), and its payload
       is the tables service's, not the legacy one's — a different `bet` shape and a wallet
       that has to stay in step with every ack, not be read once. The shared driver effect
       below owns all four of these. */
    if (shared) {
      // The identity fetch is still worth having: it is the leaderboard's YOU card and it is
      // cosmetic in both topologies.
      api.get<{ first_name?: string; username?: string; profile_image_url?: string | null }>('/me')
        .then((me) => { youRef.current = {
          name: me.first_name || me.username || 'You',
          photo: me.profile_image_url || null,
        }; })
        .catch(() => { /* cosmetic only */ });
      return () => { dead = true; };
    }
    (async () => {
      try {
        const st = await api.get<{ enabled: boolean; maintenanceMessage?: string; wallet: number;
          bet: { min: number; max: number; maxPerSpin: number } }>('/roulette/state');
        if (dead) return;
        srvBet.current = { min: st.bet.min, max: st.bet.max, maxPerSpin: st.bet.maxPerSpin };
        setSrvMin(st.bet.min);
        setBalance(st.wallet);
        walletLoaded.current = true;
        setWalletReady(true);
        if (!st.enabled) { flash(st.maintenanceMessage || 'Roulette is unavailable right now.'); }
        api.get<{ first_name?: string; username?: string; profile_image_url?: string | null }>('/me')
          .then((me) => { youRef.current = {
            name: me.first_name || me.username || 'You',
            photo: me.profile_image_url || null,
          }; })
          .catch(() => { /* cosmetic only */ });
      } catch {
        if (!dead) flash('Could not reach the table — check your connection');
      }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [secs, setSecs] = useState(WINDOW / 1000);
  /* THE BED FOLLOWS THE ROUND. This is the whole of the "make it exciting" work
     and it is deliberately four lines: one effect, one pure function, no state
     of its own. Quiet while the window is open, tightening through the last five
     seconds, wide open while the ball runs, releasing on the result — see
     intensityFor(). Nothing about the music changes, only how it is voiced, so
     there is never a switch to hear. */
  useEffect(() => { setMusicIntensity(intensityFor(phase, secs)); }, [phase, secs]);
  /** 'open' = Place Your Bet, 'shut' = Stop Betting, null = out of the way. */
  const [call, setCall] = useState<'open' | 'shut' | null>('open');
  /* THE LEADERBOARD. `seats` is always in rank order, `gains` is what each
     named player won on the round just settled — keyed by name, because the
     order is about to change and a positional key would float the money onto
     the wrong face. */
  const [seats, setSeats] = useState<Seat[]>(() => rollRoster(Math.random));
  const [gains, setGains] = useState<Record<string, number>>({});
  const [tableTarget, setTableTarget] = useState(() => pickTableTarget(Math.random));
  const windowStart = useRef(0);
  /** The magnifier, shown only while a token is actually being carried. */
  const [loupe, setLoupe] = useState<{ x: number; y: number; target: Target | null } | null>(null);
  const [sheet, setSheet] = useState<Target[] | null>(null);
  // The bet under the finger lives in STATE, not in the gesture ref: a ref
  // change does not re-render, and the status pill has to repaint every frame.
  const [preview, setPreview] = useState<PendingBet | null>(null);
  const [focusCell, setFocusCell] = useState({ r: 1, c: 0 });
  const [toast, setToast] = useState<string | null>(null);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const appRef = useRef<HTMLDivElement | null>(null);
  /** Kept only as the pinned-chrome anchor; fit() no longer measures it — the
   *  camera throw is a pure ratio now. */
  const chromeRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const feltRef = useRef<HTMLDivElement | null>(null);
  const trayRef = useRef<HTMLDivElement | null>(null);
  const timers = useRef<number[]>([]);
  const slipRef = useRef<Placed[]>([]);
  /**
   * A live mirror of `bets`, for readers that are NOT a render.
   *
   * doSpin() froze the slip from the `bets` in its closure. That was correct
   * while the SPIN button called it — the handler is recreated every render, so
   * the closure was always fresh. The 20-second timer is not: its effect is
   * keyed on [phase], so it captured `bets` at the moment the window OPENED,
   * which is always the empty array. The timer therefore settled an empty slip
   * and the player's stake was silently voided — money in, wheel spins, nothing
   * staked and nothing paid.
   *
   * Anything that fires from a timer must read this, never the closure.
   */
  const betsRef = useRef<Placed[]>([]);
  const seqRef = useRef(0);
  /** The settled balance, readable from inside a timer without capturing a
   *  stale render. */
  const balanceRef = useRef(0);
  /** The placement journal, readable from a handler armed a render ago — see `undo`. */
  const journalRef = useRef<{ id: string; amount: number; key?: string }[]>([]);
  useEffect(() => { balanceRef.current = balance; }, [balance]);
  useEffect(() => { betsRef.current = bets; }, [bets]);
  useEffect(() => { journalRef.current = journal; }, [journal]);
  const rollRef = useRef<null | (() => void)>(null);

  const betting = phase === 'betting';
  const inRound = phase !== 'betting';

  /* THE BETTING FIREWALL.
     `betting` is React state, so when the timer calls setPhase() a pointerup
     delivered in the SAME task still resolves place() against betting === true.
     One frame — and that frame is exactly the "a bet landed after the clock hit
     zero" case. Geometry cannot help either: .rlt-felt{pointer-events:none} is
     applied by the same re-render. A ref is synchronous and unbatched, so
     nothing can outrun it. */
  const bettingRef = useRef(true);
  /** The per-round spin lock. Taken synchronously by the first doSpin() of a
   *  round, released only where bettingRef is re-armed — see doSpin(). */
  const spinLockRef = useRef(false);
  /* The server's answer for the round being spun. FIRED FROM closeBetting —
     the moment the slip is final — and AWAITED in doSpin, so the ~3.4s of
     'Stop Betting' + arming beats absorb the network round-trip and the
     wheel almost never visibly waits. */
  const spinReqRef = useRef<Promise<SpinResponse> | null>(null);
  /* THE CAMERA. Up for the spin and the moment of the result; back down as the
     reveal begins, which is when the board has to be readable again. */
  const camUp = phase === 'arming' || phase === 'spinning' || phase === 'result';
  const totalBet = useMemo(() => bets.reduce((s, b) => s + b.amount, 0), [bets]);
  /* THE WALLET.
     `balance` is credited the instant the ball seats, but `bets` is not cleared
     until the round ends — so the plain `balance - totalBet` subtracted the
     stake a SECOND time for the whole result window. Win 1000 on a 500 stake
     from 10,000 and the header sat at 10,000 for ~5s before snapping to 10,500,
     which reads as "my winnings vanished and came back".
     Once a winner exists the bets are settled history, so nothing is on the
     table any more; the only thing still held back is the credit the count-up
     has not yet paid out. */
  /* SHARED MODE subtracts nothing for the felt: the server debited every chip at the moment it
     was placed, so `balance` is ALREADY net of the slip. Subtracting `totalBet` again would
     show a player half their money for the length of a betting window. `creditPending` still
     applies in both topologies — that is not the stake, it is the win the count-up has not
     visibly paid in yet. (2026-09-06.) */
  const purse = winner != null && inRound
    ? balance - creditPending
    : (shared ? balance : balance - totalBet);
  /* Mirrored for the visibility snap below: the snap used to write the RAW
     balance, and because React diffs against its own vdom (which still said
     purse), the wrong number then STUCK until purse's value next changed --
     a stake on the felt shown as refunded for a whole window. (Recheck P1.) */
  const purseRef = useRef(purse);
  purseRef.current = purse;
  /** Same trick for the ready flag: the visibility snap runs from a listener bound once. */
  const walletReadyRef = useRef(walletReady);
  walletReadyRef.current = walletReady;

  const after = useCallback((ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms) as unknown as number);
  }, []);
  useEffect(() => () => { timers.current.forEach(clearTimeout); rollRef.current?.(); }, []);

  /* ─── the money landing ────────────────────────────────────────────────
     ONE short-lived rAF writing textContent on a ref'd span. Deliberately NOT
     per-frame setState: Board is 49 cells plus a full SVG overlay, and the
     wheel's own rAF is running at the same time. Two loops total, never three. */
  const countRaf = useRef(0);
  const countUp = useCallback((from: number, to: number) => {
    cancelAnimationFrame(countRaf.current);
    const el = walletRef.current;
    if (!el) return;
    const t0 = performance.now();
    const step = () => {
      const k = Math.min(1, (performance.now() - t0) / COUNT_DUR_MS);
      const eased = 1 - Math.pow(1 - k, 3);          // easeOutCubic
      el.textContent = Math.round(from + (to - from) * eased).toLocaleString('en-IN');
      if (k < 1) countRaf.current = requestAnimationFrame(step);
    };
    step();
  }, []);
  // A hidden tab pauses rAF mid-count; snap to the truth on return and on
  // unmount so the wallet can never be left showing a half-counted number.
  useEffect(() => {
    const snap = () => {
      if (document.hidden) return;
      cancelAnimationFrame(countRaf.current);
      // …and it must not snap to a figure the server has not confirmed either: before the
      // first `/state` the pill is an em dash and has to stay one. (2026-09-06.)
      if (walletRef.current) {
        walletRef.current.textContent = walletReadyRef.current
          ? purseRef.current.toLocaleString('en-IN') : '\u2014';
      }
    };
    document.addEventListener('visibilitychange', snap);
    return () => { document.removeEventListener('visibilitychange', snap); cancelAnimationFrame(countRaf.current); };
  }, []);

  /* ─── coins into the wallet ────────────────────────────────────────────
     WAAPI, no library and no new asset: the flying coin is literally the
     wallet's own coin image, so it reads as the same money. Launched from the
     chips that actually won, which are already flagged and rendered
     .rlt-chip-won.

     The rects are read ONCE at launch and thrown away — never cached, never
     observed. A cached rect in this game is what broke placement once already. */
  const flyCoins = useCallback(() => {
    const wallet = walletRef.current;
    if (!wallet || typeof document === 'undefined') return;
    const el = document.createElement('div');
    if (!(el as HTMLElement).animate) return;                 // graceful skip
    const target = wallet.getBoundingClientRect();
    const from = [...document.querySelectorAll('.rlt-chip-won')].slice(0, 6);
    const srcs = from.length ? from : [document.querySelector('.rlt-grid')].filter(Boolean);
    const layer = document.createElement('div');
    layer.className = 'rlt-coinfly';
    document.body.appendChild(layer);
    srcs.forEach((node, i) => {
      const r = (node as HTMLElement).getBoundingClientRect();
      const c = document.createElement('img');
      c.src = '/games/ludo/Coin.png';
      c.className = 'rlt-flycoin';
      c.style.left = `${r.left + r.width / 2}px`;
      c.style.top = `${r.top + r.height / 2}px`;
      layer.appendChild(c);
      const dx = target.left + target.width / 2 - (r.left + r.width / 2);
      const dy = target.top + target.height / 2 - (r.top + r.height / 2);
      c.animate([
        { transform: 'translate(-50%,-50%) scale(.6)', opacity: 0 },
        { transform: `translate(calc(-50% + ${dx * 0.5}px), calc(-50% + ${dy * 0.5 - 60}px)) scale(1.15)`, opacity: 1, offset: .5 },
        { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(.45)`, opacity: 0 },
      ], { duration: 620, delay: i * 90, easing: 'cubic-bezier(.3,.7,.4,1)', fill: 'forwards' });
    });
    window.setTimeout(() => layer.remove(), 620 + srcs.length * 90 + 120);
  }, []);

  /* ─── lattice measurement ───────────────────────────────────────────────
     Measured from the grid element, never from a cell: a cell's rect is
     device-pixel-rounded and the accumulated error lands exactly on the right
     border, where street and six line live. */
  const measure = useCallback(() => {
    const grid = gridRef.current, root = rootRef.current;
    if (!grid) return;
    // One fifth of the measured grid — the board is five EQUAL columns.
    const rail = layoutBox(grid).width * RAIL_FRAC;
    setRailPx(rail);
    const L = measureLattice(grid, rail, rail, GAP);
    setLattice(L);
    if (!root) return;

    // The numeral is sized from the CELL, never from the viewport. It was
    // clamp(11px, 3.4vw, 16px), which is a measurement of the window and has
    // nothing to do with the disc it has to fit inside: at one common size it
    // rendered 22.4px wide inside a 21.9px disc — the number was wider than the
    // circle it sat in. Driving it off cellH fixes the ratio at every size.
    root.style.setProperty('--rlt-cellh', `${L.cellH}px`);

    // Below this the disc motif stops being legible (disc under ~22px, numeral
    // under ~9px) and the board reverts to flat filled cells. Ship the readable
    // board rather than an illegible faithful one.
    // layoutBox, not a client rect: rotated, the rect reports width AS height
    // and a phone in landscape silently lost the disc design. (Audit C5.)
    const tight = layoutBox(grid).height < 420;
    root.dataset.tight = tight ? '1' : '0';
  }, []);
  /* ─── fit(): ONE place derives the whole camera ────────────────────────
     Measured off .rlt-app, never off `window`. Inside .force-portrait the page
     is rotated -90deg with width:100dvh, so window.innerWidth is the LONG edge
     — reading it here would roughly double the wheel on a phone. The old code
     got away with it only because a hard 340px cap swallowed the error; the
     moment the diameter became proportional it would have broken.

       D     cabinet diameter
       OVER  how far the wheel pokes above the canvas at rest
             = D/2 − hub, so the hub lands exactly HUB_FRAC down the screen
       PAN   the throw: lift the whole wheel clear, then clear the pinned chrome */
  useEffect(() => {
    const fit = () => {
      const app = appRef.current, root = rootRef.current;
      if (!app || !root) return;
      // layoutBox, NOT getBoundingClientRect: see the note on layoutBox. Under
      // .force-portrait's landscape rotation a client rect returns width and
      // height swapped, which scaled the entire game by the aspect ratio.
      const r = layoutBox(app);
      if (!(r.width > 0) || !(r.height > 0)) return;

      const D = Math.max(D_MIN, Math.min(D_MAX, Math.round(D_FRAC * r.width)));
      const hub = Math.round(HUB_FRAC * r.height);
      const over = Math.max(0, Math.round(D / 2 - hub));

      // The CANVAS's own size, published as vars. Everything in the reference is
      // a fraction of a 576x1280 frame, and `vw`/`vh` cannot express that here:
      // inside .force-portrait the page is rotated -90deg with width:100dvh, so
      // `vw` is the phone's LONG edge. Any reference ratio therefore has to be
      // taken against these, never against the viewport units.
      root.style.setProperty('--rlt-w', `${Math.round(r.width)}px`);
      root.style.setProperty('--rlt-h', `${Math.round(r.height)}px`);

      /* SIGN WEIGHT COMPENSATION — the fix for "the big texts look beautiful on
         desktop but go slim on mobile".
         The signs are one SVG drawing scaled to fit, so at 390px wide it renders
         at 68% of the reference 576px and the outline that gives the letters
         their heft thins by the same 68%. Geometrically that is correct and
         perceptually it is wrong: a thick stroke survives being scaled down, a
         hairline is eaten by antialiasing, so the smaller the screen the weaker
         the words look — exactly backwards from what a five-second countdown
         needs. This factor grows as the canvas shrinks so the stroke's RENDERED
         weight stays put. Capped at 1.75 so a very narrow phone does not close
         up the counters inside the letters. */
      root.style.setProperty('--rlt-sgw',
        String(Math.min(1.75, Math.max(1, 576 / Math.max(1, r.width)))));

      root.style.setProperty('--rlt-d', `${D}px`);
      root.style.setProperty('--rlt-over', `${over}px`);
      // THE THROW IS DERIVED, not a constant: it is whatever carries the hub from
      // its resting height to the spin height. With the reference's own HUB_FRAC
      // (0.0664) this evaluates to 0.1227 H = the +157 of 1280 measured four ways
      // in ref4 (the green "0" cell 350..385 -> 507..543, the players badge, the
      // Record disc cores, and avatar 1's name rows). Now that the resting hub has
      // been lifted for the board's sake, the throw grows by the same amount and
      // the panned-up frame is unchanged.
      //
      // There used to be a `max(over + clear, ratio)` here, where `clear` was the
      // pinned button row's height — and at every real phone size the floor won and
      // overshot: 161 against 104 at 390x844. It only failed to fire at the
      // reference's own 576x1280, which is why it survived three earlier passes.
      root.style.setProperty('--rlt-pan', `${Math.round((HUB_UP_FRAC - HUB_FRAC) * r.height)}px`);
      setWheelPx(D);
    };
    fit();
    // ResizeObserver on the CANVAS is correct here: this reads a size, not a
    // position. The rule it must never break is the other one — nothing in the
    // placement path may cache a position.
    const ro = new ResizeObserver(fit);
    if (appRef.current) ro.observe(appRef.current);
    window.addEventListener('orientationchange', fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', fit);
    };
  }, []);

  /* ─── the 20-second betting window ────────────────────────────────────
     Derived from performance.now(), never accumulated, so a slow frame or a
     backgrounded tab cannot make the clock drift away from the round. */
  useEffect(() => {
    /* SHARED MODE: this whole clock belongs to the server. `sharedDrive` below derives the
       countdown, the call banner and the money boundary from `locks_at` — running this timer
       as well would give the table two clocks, and the one that closed first would decide,
       which is the definition of a bet that lands after the window. */
    if (shared) return;
    if (phase !== 'betting') return;
    bettingRef.current = true;
    setSecs(WINDOW / 1000);
    setCall('open');                       // "Place Your Bet"

    let t = 0;
    const tick = () => {
      const left = WINDOW - (performance.now() - windowStart.current);
      setSecs(Math.max(0, Math.ceil(left / 1000)));
      if (left <= 0) { closeBetting(); return; }
      t = window.setTimeout(tick, 200);
    };

    // The call clears FIRST, and the clock starts on its way out — the
    // countdown is the betting window, not the announcement.
    const intro = window.setTimeout(() => {
      setCall(null);
      windowStart.current = performance.now();
      tick();
    }, INTRO_MS);

    /* CANCEL FIRST -- the same rule Wheel.tsx's visibility handler documents.
       tick() ends by scheduling itself, so calling it while a chain is pending
       starts a SECOND chain, and `t` only ever holds the newest handle. Two
       tab-switches in one window left three concurrent chains, and at zero each
       of them called closeBetting() -- which settled the same slip twice: the
       stake debited twice, two rows in My Bets, the wheel restarting mid-arm.
       Found by the 2026-08-16 audit (three lenses independently). */
    const resync = () => {
      if (!document.hidden && windowStart.current) { window.clearTimeout(t); tick(); }
    };
    document.addEventListener('visibilitychange', resync);
    return () => {
      window.clearTimeout(intro); window.clearTimeout(t);
      document.removeEventListener('visibilitychange', resync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, WINDOW]);

  /* THE ALL-BETS SHEET DIES WITH THE WINDOW IT BELONGS TO. It is a menu of
     bets, and every row goes through place(), which bettingRef correctly
     refuses once the window shuts -- so a sheet left open past the close was
     a menu of dead rows that LOOKED alive: taps did nothing, silently. Close
     it at the same boundary that invalidates it. (2026-08-16 audit, C4.) */
  useEffect(() => { if (phase !== 'betting') setSheet(null); }, [phase]);

  /* GOING BROKE IS NOT A DEAD END. Below the smallest chip the player cannot
     act at all, and the old build simply sat there: every tap said 'pick a
     token' and nothing said why or what to do. Offer the refill where the
     problem is (audit C10). Once per window -- phase re-entry re-runs this,
     which is exactly the cadence a broke player should be re-offered at. */
  useEffect(() => {
    // Below MIN_BET, not below 10: at a min-Rs50 table a player holding Rs40
    // is exactly as stuck, and was exactly as unrescued. (Recheck P9.)
    if (phase === 'betting' && walletLoaded.current && balance < MIN_BET) setTopup(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  /* Promote the stage only while it is actually moving. */
  const [camMoving, setCamMoving] = useState(false);
  useEffect(() => {
    setCamMoving(true);
    const t = window.setTimeout(() => setCamMoving(false), PAN_MS + 60);
    return () => window.clearTimeout(t);
  }, [camUp]);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (gridRef.current) ro.observe(gridRef.current);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); window.removeEventListener('orientationchange', measure); };
  }, [measure]);

  /* ─── the one highlight mechanism, three reasons ────────────────────────
     winner outranks preview outranks confirm, so a stuck preview can never
     hide the result the player is waiting for. */
  const PRIORITY: HlReason[] = ['winner', 'preview', 'confirm'];
  const highlight = useMemo(
    () => PRIORITY.map((r) => hls[r]).find(Boolean) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hls],
  );
  /* Every OUTSIDE bet that just won, so the rails wash gold alongside the
     number. Derived from bet.numbers via winningZones(), which is the same
     array the money layer pays from — the lights cannot disagree with the
     payout. Empty outside the result hold, so the board is clean while betting. */
  const winIds = useMemo(
    () => (winner != null && zonesLit ? winningZones(winner).ids : EMPTY_IDS),
    [winner, zonesLit],
  );

  const litCells = useMemo(() => {
    const s = new Set<string>();
    // NOT the winner. `.rlt-cell.lit` paints a 2px pure-white inset ring, and
    // during the result `highlight` IS hls.winner — so the winning cell carried
    // that ring under the yellow wash for the whole hold, compositing to
    // (250,250,144). MEASURED: the reference's winning-cell inner edge is
    // (153,193,75) one pixel in and plain wash beyond, i.e. no white band at all.
    if (highlight && highlight.reason !== 'winner') {
      for (const c of highlight.cells) s.add(`${c.r}:${c.c}`);
    }
    return s;
  }, [highlight]);

  const showHl = useCallback((reason: HlReason, bet: PendingBet, marker?: { x: number; y: number } | null, ms?: number) => {
    const seq = ++seqRef.current;
    setHls((s) => ({ ...s, [reason]: { reason, cells: cellsForNumbers(bet.numbers), marker: marker ?? null, seq } }));
    if (ms) after(ms, () => setHls((s) => (s[reason]?.seq === seq ? { ...s, [reason]: undefined } : s)));
  }, [after]);
  const clearHl = useCallback((reason: HlReason) => setHls((s) => ({ ...s, [reason]: undefined })), []);

  /* ─── placement ─────────────────────────────────────────────────────── */

  const amountOf = useCallback((id: string) => bets.find((b) => b.id === id)?.amount ?? 0, [bets]);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    after(1800, () => setToast((t) => (t === msg ? null : t)));
  }, [after]);

  /**
   * Where each position's chip was last drawn (title, sub, felt coordinates).
   *
   * Needed only by the shared path's carry-over: a chip the lock refused has to be laid back
   * on the felt NEXT round, and by then the slip that knew where it sat has been cleared. A
   * ref because nothing renders from it — it is a lookup table for putting a chip back on the
   * square it came off. (2026-09-06.)
   */
  const lastPlacedRef = useRef(new Map<string, { title: string; sub: string; fx: number | null; fy: number | null; centre: boolean }>());

  /**
   * PUT ONE CHIP ON THE FELT.
   *
   * Lifted out of `place()` on 2026-09-06 so three callers can share it: a tap, REBET, and the
   * shared path's carry-over. The two state writes below are the shipped ones, character for
   * character; what is new is the `shared.place()` in front of them, which is the whole of
   * §5.7's "chips are posted per placement".
   *
   * ORDER MATTERS. The server is asked FIRST, and the felt is written only if the placement
   * was accepted into the debounce buffer. Drawing before that could put a chip on a shut
   * table; drawing after the ACK would lag the tap by a round trip. Between those two, the
   * honest moment to draw it is when the money is spoken for — which is exactly why it is
   * drawn TRANSLUCENT until the ack lands (see `PlacedChip.ack`).
   *
   * `post: false` places a chip WITHOUT telling the server. It exists for one caller and one
   * only: nothing uses it today, and it is kept because a future "preview the slip" affordance
   * would need it. Every live path posts.
   */
  const addChip = useCallback((row: {
    id: string; kind: BetKind; numbers: number[]; title: string; sub: string;
    fx: number | null; fy: number | null; centre: boolean;
  }, amount: number, opts?: { post?: boolean }): boolean => {
    let key: string | undefined;
    if (shared && opts?.post !== false) {
      const res = shared.place({ posId: row.id, kind: row.kind, numbers: row.numbers, amount });
      if (!res.ok) {
        flash(res.reason === 'locked'
          ? 'Betting has closed for this spin'
          : 'Not enough balance — pick a smaller chip');
        navigator.vibrate?.(12);
        sDenied();
        return false;
      }
      key = res.key;
    }
    lastPlacedRef.current.set(row.id, {
      title: row.title, sub: row.sub, fx: row.fx, fy: row.fy, centre: row.centre,
    });
    setBets((prev) => {
      const i = prev.findIndex((b) => b.id === row.id);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], amount: next[i].amount + amount };
        return next;
      }
      return [...prev, {
        id: row.id, kind: row.kind, numbers: row.numbers, amount,
        title: row.title, sub: row.sub,
        fx: row.fx,
        fy: row.fy,
        centre: row.centre,
      }];
    });
    setJournal((j) => [...j, { id: row.id, amount, key }]);
    return true;
  }, [shared, flash]);

  const place = useCallback((bet: PendingBet, amount: number, marker?: { x: number; y: number } | null, centre = false) => {
    if (!bettingRef.current) return false;   // authoritative, synchronous
    if (!betting) return false;              // defence in depth

    if (!walletLoaded.current) {
      // The balance on screen is not the wallet yet; a bet against it would
      // be a guess. One beat later the fetch lands and betting opens.
      flash('Connecting to your wallet\u2026');
      return false;
    }

    if (amount < MIN_BET) {
      flash(`Minimum bet at this table is ₹${MIN_BET}`);
      navigator.vibrate?.(12);
      sDenied();
      return false;
    }

    /* WHAT IS LEFT TO SPEND — and it is not the same subtraction in the two topologies.
       PER-USER: the money leaves at the SPIN, so the chips on the felt are still inside
       `balance` and have to be taken off it here.
       SHARED: the money leaves at the PLACEMENT — the server debited it and the clock's
       balance is already net of every chip, optimistic holds included — so subtracting the
       slip a second time would refuse a player their own next chip. (2026-09-06.) */
    const available = shared ? balance : balance - totalBet;
    if (amount > available) {
      // The first build just `return`ed here — a tap that did nothing, which a
      // player reads as "the game is broken", not "I am short".
      flash('Not enough balance — pick a smaller chip');
      navigator.vibrate?.(12);
      sDenied();
      return false;
    }
    const L = lattice;
    if (!addChip({
      id: bet.id, kind: bet.kind, numbers: bet.numbers,
      title: bet.title, sub: bet.sub,
      fx: marker && L ? marker.x / pitchX(L) : null,
      fy: marker && L ? marker.y / pitchY(L) : null,
      centre,
    }, amount)) return false;
    navigator.vibrate?.(10);
    sChipPlace();
    // R7: the same highlight becomes a confirmation flash, so pressing EVEN
    // leaves all 18 evens lit long enough to read.
    showHl('confirm', bet, marker, CONFIRM_MS);
    return true;
  }, [betting, balance, totalBet, lattice, showHl, flash, addChip, shared]);

  /** Undo pops the last PLACEMENT, not the last array entry. The first build
   *  used bets.slice(0,-1), so tapping 17, then RED, then 17 again deleted RED. */
  const undo = useCallback(() => {
    // The same authoritative guard place() carries: React's disabled attribute
    // commits a frame later than bettingRef flips, and a pointerup in that gap
    // must not edit a slip the settle is about to freeze. (Recheck P2.)
    if (!bettingRef.current) return;
    if (shared) {
      /* SHARED MODE: a placement is a `shared_round_bets` ROW, and a row is atomic — there is
         no partial `/unbet`. So Undo takes back the row the last tap went into, which is the
         last tap PLUS any taps that merged with it inside the 250 ms debounce window (they
         were one request and they are one bet). Taps far enough apart to have been sent
         separately come back one at a time, exactly as they went out. The player's mental
         model and the server's grain are then the same thing.

         Read from the ref, never the render's closure: this handler is also reachable from a
         keyboard binding armed a render ago. (2026-09-06.) */
      const j = journalRef.current;
      const last = j[j.length - 1];
      if (!last) return;
      sChipOff();
      const going = last.key ? j.filter((e) => e.key === last.key) : [last];
      const amt = going.reduce((s, e) => s + e.amount, 0);
      // A carry-over chip that was never posted (no key) is purely local — there is nothing to
      // ask the server to give back, because it never took it.
      if (last.key) shared.unbetKey(last.key);
      setJournal((prev) => prev.filter((e) => !going.includes(e)));
      setBets((prev) => prev.flatMap((b) => {
        if (b.id !== last.id) return [b];
        const left = b.amount - amt;
        return left > 0 ? [{ ...b, amount: left }] : [];
      }));
      return;
    }
    sChipOff();
    setJournal((j) => {
      const last = j[j.length - 1];
      if (!last) return j;
      setBets((prev) => prev.flatMap((b) => {
        if (b.id !== last.id) return [b];
        const amt = b.amount - last.amount;
        return amt > 0 ? [{ ...b, amount: amt }] : [];
      }));
      return j.slice(0, -1);
    });
  }, [shared]);

  const clear = useCallback(() => {
    if (!bettingRef.current) return;
    sChipOff();
    // Shared mode: every chip on this round is a real bet the server is holding, so clearing
    // the felt means asking for all of them back. The clock fans the refunds out one `/unbet`
    // per row and hands the money back optimistically; a refusal puts the chip back on screen
    // rather than showing rupees the player does not have.
    if (shared) shared.unbetAll();
    setBets([]); setJournal([]);
  }, [shared]);

  const rebet = useCallback(() => {
    if (!bettingRef.current) return;
    sTap();
    // It used to return in silence here, which is indistinguishable from a dead
    // button — and this is the state it is in for the whole of a player's first
    // round, because there is no previous bet to repeat yet.
    if (!lastBets.length) { flash('Nothing to repeat yet — this repeats your last round'); return; }
    const need = lastBets.reduce((s, b) => s + b.amount, 0);
    // `balance` is already net of the felt in shared mode and gross of it locally, and REBET
    // only runs on an empty felt in practice, so the same comparison is right in both.
    if (need > balance) { flash('Not enough balance to repeat that'); return; }
    if (shared) {
      // Every repeated chip is a fresh PLACEMENT with its own idempotency key: the previous
      // round's rows belong to a wheel that has already turned and cannot be re-used.
      for (const b of lastBets) {
        addChip({
          id: b.id, kind: b.kind, numbers: b.numbers, title: b.title, sub: b.sub,
          fx: b.fx, fy: b.fy, centre: b.centre,
        }, b.amount);
      }
      return;
    }
    setBets(lastBets.map((b) => ({ ...b })));
    setJournal(lastBets.map((b) => ({ id: b.id, amount: b.amount })));
  }, [lastBets, balance, flash, shared, addChip]);

  /* ─── placement: DOM-anchored, tap OR drag ──────────────────────────── */

  const tap = useRef<{ id: number; x0: number; y0: number; ok: boolean } | null>(null);
  const carry = useRef<{ id: number; value: number; armed: boolean; x0: number; y0: number } | null>(null);
  const longT = useRef(0);
  const holdT = useRef(0);
  const lastAim = useRef<string | null>(null);

  /** The cell under a point, straight from the browser. Probes a small cross so
   *  the 3px gaps between cells and the outer border — where the street and
   *  six-line studs sit — are not dead zones. */
  const cellUnder = (x: number, y: number): HTMLElement | null => {
    for (const [dx, dy] of [[0, 0], [-5, 0], [5, 0], [0, -5], [0, 5], [-5, -5], [5, -5], [-5, 5], [5, 5]]) {
      const el = document.elementFromPoint(x + dx, y + dy) as HTMLElement | null;
      const cell = el?.closest<HTMLElement>('[data-n]');
      if (cell) return cell;
    }
    return null;
  };

  const railAt = (x: number, y: number): PendingBet | null => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const b = el?.closest<HTMLElement>('[data-bet-kind]');
    if (!b || b.dataset.disabled === '1') return null;
    return makeBet(b.dataset.betKind as BetKind, b.dataset.betNumbers!.split(',').map(Number), b.dataset.betTitle!);
  };

  /** What a chip dropped here would buy. `wide` opens the edge bands up for a
   *  drag, which has a live preview to correct against; a tap keeps them tight
   *  so a careless one still lands on the number. */
  const aimAt = useCallback((x: number, y: number, wide = false): PendingBet | null => {
    const rail = railAt(x, y);
    if (rail) return rail;
    const cell = cellUnder(x, y);
    if (!cell || !lattice) return null;

    const n = Number(cell.dataset.n);
    const q = cell.getBoundingClientRect();       // the LIVE rect of this cell
    /* IN BOARD AXES, NOT VIEWPORT AXES. Under the landscape rotation a client
       rect comes back with its axes swapped, and edge tests written against
       raw x/y aim at the wrong board line -- a split between side-by-side
       numbers landed on the vertically adjacent pair instead. cellFrac() maps
       the pointer into the cell in the board's own axes at every rotation,
       so everything below is rotation-blind. (2026-08-16 audit, C3.) */
    const { u, v, spanU, spanV } = cellFrac(q, x, y);
    const RX = wide ? 16 : 12;
    const RY = wide ? 12 : 8;

    let r: number, c: number;
    if (n === 0) {
      r = 0;
      c = Math.max(0, Math.min(2, Math.floor(u * 3)));
    } else {
      r = Math.floor((n - 1) / 3) + 1;
      c = (n - 1) % 3;
    }

    // Only the INTERIOR lines are live, and they are exactly the ones drawn as
    // studs — what you see is what you can bet. Column 0's left edge borders the
    // dozen rail, column 2's right edge is the outside of the board, and the
    // line under the 0 bar is not a target either. The bets those edges stood
    // for (street, six line, the zero splits) live in the long-press sheet.
    let vi: number | null = null;
    if (spanU * (1 - u) <= RX && c < 2) vi = c + 1;
    else if (spanU * u <= RX && c > 0) vi = c;

    let hi: number | null = null;
    if (spanV * (1 - v) <= RY && r >= 1 && r < 12) hi = r + 1;
    else if (spanV * v <= RY && r >= 2) hi = r;
    if (r === 0) { vi = null; hi = null; }        // the 0 bar is a plain cell

    return targetAtCell(lattice, r, c, vi, hi);
  }, [lattice]);

  const showAim = useCallback((t: PendingBet | null) => {
    setPreview(t);
    if (t) {
      showHl('preview', t, (t as Target).marker ?? null);
      if (t.id !== lastAim.current) { lastAim.current = t.id; sAimTick(); navigator.vibrate?.(5); }
    } else { clearHl('preview'); lastAim.current = null; }
  }, [showHl, clearHl]);

  const endAim = useCallback(() => {
    clearHl('preview'); setPreview(null); setLoupe(null); lastAim.current = null;
  }, [clearHl]);

  /* ── tapping the board ── */
  const onBoardDown = (e: React.PointerEvent) => {
    if (!e.isPrimary) return;
    unlockSound();
    unlockSpeech();   // iOS only speaks later if the FIRST utterance came from a gesture
    if (!betting) return;
    if (chip == null) { flash('Pick a token first'); sDenied(); return; }
    tap.current = { id: e.pointerId, x0: e.clientX, y0: e.clientY, ok: true };
    showAim(aimAt(e.clientX, e.clientY));
    longT.current = window.setTimeout(() => {
      const g = tap.current; if (!g || !g.ok || !lattice) return;
      const at = aimAt(g.x0, g.y0);
      if (!at) return;
      const cells = cellsForNumbers(at.numbers);
      tap.current = null; endAim();
      setSheet(betsAtCell(lattice, cells[0].r, cells[0].c));
      navigator.vibrate?.(16); sTap();
    }, LONGPRESS_MS);
  };

  const onBoardMove = (e: React.PointerEvent) => {
    const g = tap.current;
    if (g && e.pointerId === g.id) {
      if (Math.hypot(e.clientX - g.x0, e.clientY - g.y0) > TAP_SLOP) {
        g.ok = false; window.clearTimeout(longT.current); endAim();
      }
      return;
    }
    // ── carrying a token out of the tray ──
    const d = carry.current;
    if (!d || e.pointerId !== d.id) return;
    if (!d.armed) {
      if (Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < DRAG_SLOP) return;
      d.armed = true; window.clearTimeout(holdT.current);
    }
    const t = aimAt(e.clientX, e.clientY, true);
    showAim(t);
    /* Wrapper-local, not viewport: the loupe is position:fixed INSIDE the
       rotated wrapper, so its left/top anchor there. In portrait this is the
       identity; in landscape it is what keeps the loupe on the fingertip. */
    const lp = toWrapperLocal(e.clientX, e.clientY);
    setLoupe({ x: lp.x, y: lp.y, target: (t as Target)?.cells ? (t as Target) : null });
  };

  const onBoardUp = (e: React.PointerEvent) => {
    const g = tap.current;
    if (g && e.pointerId === g.id) {
      window.clearTimeout(longT.current);
      tap.current = null; endAim();
      if (!g.ok || chip == null) return;
      const t = aimAt(e.clientX, e.clientY);
      if (t) place(t, chip, (t as Target).marker ?? null, (t as Target).snap === 'centre');
      return;
    }
    const d = carry.current;
    if (!d || e.pointerId !== d.id) return;
    window.clearTimeout(holdT.current);
    carry.current = null; endAim();
    if (!d.armed) return;                       // a tray tap just selects
    const t = aimAt(e.clientX, e.clientY, true);
    if (t) place(t, d.value, (t as Target).marker ?? null, (t as Target).snap === 'centre');
  };

  const onBoardCancel = () => {
    window.clearTimeout(longT.current); window.clearTimeout(holdT.current);
    tap.current = null; carry.current = null; endAim();
  };

  /* ── picking a token up ── */
  const onTokenDown = (e: React.PointerEvent, v: number) => {
    if (!e.isPrimary || !betting) return;
    unlockSound();
    unlockSpeech();   // iOS only speaks later if the FIRST utterance came from a gesture
    /* TAP THE SELECTED CHIP AGAIN = PUT IT DOWN. Without this, a player who
       changed their mind carried a live chip they could only get rid of by
       betting it — one stray tap on the felt away from money they did not mean
       to stake. (Owner, 2026-09-01.) */
    if (chip === v) {
      setChip(null); sChipOff(); navigator.vibrate?.(6);
      return;
    }
    setChip(v); sChipPick(); navigator.vibrate?.(6);
    if (v > purse) return;                      // nothing to carry
    try { trayRef.current?.setPointerCapture(e.pointerId); } catch { /* already gone */ }
    carry.current = { id: e.pointerId, value: v, armed: false, x0: e.clientX, y0: e.clientY };
    holdT.current = window.setTimeout(() => { const d = carry.current; if (d) d.armed = true; }, HOLD_MS);
  };

  const onKeyPlace = (r: number, c: number, e: React.KeyboardEvent) => {
    const L = lattice; if (!L) return;
    const nav: Record<string, [number, number]> = {
      ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1],
    };
    if (nav[e.key]) {
      e.preventDefault();
      const [dr, dc] = nav[e.key];
      const nr = Math.max(0, Math.min(12, r + dr));
      const nc = Math.max(0, Math.min(2, c + dc));
      setFocusCell({ r: nr, c: nc });
      gridRef.current?.querySelector<HTMLElement>(`[data-n="${nr === 0 ? 0 : (nr - 1) * 3 + nc + 1}"]`)?.focus();
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (chip == null) { flash('Pick a token first'); sDenied(); return; }
      const n = r === 0 ? 0 : (r - 1) * 3 + c + 1;
      const b = makeBet('straight', [n], `Number ${n}`);
      const marker = {
        x: r === 0 ? (3 * L.cellW + 2 * L.gap) / 2 : c * pitchX(L) + L.cellW / 2,
        y: r * pitchY(L) + L.cellH / 2,
      };
      place(b, chip, marker, true);
    }
  };

  /* ─── the round ─────────────────────────────────────────────────────── */

  /**
   * THE RESULT SEQUENCE — six beats, measured from the instant the ball seats (t = 0).
   *
   * Lifted verbatim out of `doSpin` on 2026-09-06 so the shared-table path can play the same
   * seven seconds off the server's `settles_at`. Nothing in here decides anything: it takes
   * the number, whether the player won, and the two rupee figures the banner and the count-up
   * need, and it schedules the presentation. Every decision — what was staked, what came back,
   * what the wallet now holds, what goes in the Record — belongs to the caller, because that
   * is precisely what a per-user spin and a shared round disagree about.
   *
   *      0   seat (the caller's job)
   *   1200   camera pans DOWN; the result inset slides in; the number is CALLED
   *   1460   every winning zone washes gold
   *   4460   zones clear                       <- the hold is 3000 ms, which the spec requires
   *   4560   the leaderboard re-sorts
   *   4700   "YOU HAVE WON" + coins launch from the winning chips
   *   4790   the wallet starts counting up
   */
  const resultBeats = useCallback((o: {
    n: number; won: boolean; bannerAmount: number; returned: number;
  }) => {
    const { n, won, bannerAmount, returned } = o;

    /* ── t = 1200 — the camera pans back down, the inset slides in ── */
    after(RESULT_HOLD_MS, () => {
      setPhase('reveal');
      // AND THE CALL. "Number thirteen, black" — on the same beat the result
      // card arrives, which is where the player's eyes already are. The
      // colour comes from the engine's own colourOf(), so the spoken call
      // can never disagree with the disc on the card.
      // Pull the music bed down under it. The call is the one piece of
      // audio in this game that carries information; it does not compete.
      // duckMusic is called from HERE rather than from inside sCallNumber so
      // that sound.ts never has to import music.ts — music.ts imports the
      // shared AudioContext from sound.ts, and a cycle between the two is
      // exactly the kind of thing that works in dev and breaks in a build.
      /* Duck ONLY when the call will actually speak. Unconditional, this
         dropped the bed to a fifth for ~3 silent seconds whenever Voice
         was off — which the owner heard, precisely, as "the background
         music stops when the result is announced". */
      if (isVoiceOn() && !document.hidden) duckMusic();
      sCallNumber(n, colourOf(n));
    });

    /* ── t = 1460 — every winning zone washes gold ── */
    after(ZONES_IN_MS, () => setZonesLit(true));

    /* ── t = 4460 — and clears. The gap is the 3000ms the spec requires. ── */
    after(ZONES_OUT_MS, () => setZonesLit(false));

    /* ── t = 4560 — the leaderboard settles ──────────────────────
         The wins land and the order re-sorts, in front of the player, on
         the same beat their own payout lands. That is the beat where they
         are already looking at money, and a standing that visibly moves is
         a standing that gets watched. */
    after(4560, () => {
      setSeats((prev) => {
        const { seats: next, gains: g } = applyRoundWins(prev, Math.random);
        setGains(g);
        return next;
      });
    });

    /* ── t = 4700 — the player finds out ── */
    after(WIN_TEXT_MS, () => {
      setBanner({ won, amount: bannerAmount });
      /* The moment of truth gets its own audio event — the bed steps aside
         for a second so the sting actually lands (they were playing, but
         underneath the music, which read as "no special sound"). */
      duckMusic(900);
      if (won) { sWin(); navigator.vibrate?.([12, 60, 12, 60, 24]); flyCoins(); }
      else { sLose(); navigator.vibrate?.(24); }
    });

    /* ── t = 4790 — the money visibly lands ── */
    if (won) {
      after(COUNT_UP_MS, () => {
        const from = balanceRef.current - returned;
        countUp(from, balanceRef.current);
        after(COUNT_DUR_MS + 40, () => setCreditPending(0));
      });
    }
  }, [after, countUp, flyCoins]);

  /**
   * Close the table. ONE function, and the ref flips FIRST.
   *
   * Everything after the ref is cosmetic; the ref is the money boundary. It
   * also kills any gesture that is mid-flight, because a finger already down
   * when the clock hits zero would otherwise complete its tap into a closed
   * table.
   */
  function closeBetting() {
    /* SHARED MODE NEVER CLOSES ITS OWN WINDOW. The window belongs to `locks_at`, and this
       function's job — freeze the slip, POST it to `/spin` — is the per-user topology's whole
       money path. Nothing calls it in shared mode (the timer that did is disabled), so this is
       defence in depth against a stray timer surviving a topology flip mid-session; without it
       such a timer would post a slip to a route that answers `wrong_topology`. */
    if (shared) return;
    // IDEMPOTENT, and the ref is what makes it so: it flips false on the first
    // call and is only re-armed when the next betting window opens, so a second
    // close of the same window -- from a surviving timer chain, a race,
    // anything -- is provably invalid and returns without touching the round.
    if (!bettingRef.current) return;
    // The money boundary is HERE, at the stroke of zero — not at the pan, and
    // not when the spin starts. Everything after this line is presentation.
    bettingRef.current = false;
    window.clearTimeout(longT.current);
    window.clearTimeout(holdT.current);
    tap.current = null;
    carry.current = null;
    endAim();

    /* THE MONEY LEAVES HERE. The slip is final (the ref just closed), so the
       server round fires now, concurrently with the Stop-Betting beat. An
       empty slip stays local — the server has nothing to debit and the wheel
       only needs a number to animate. */
    spinReqRef.current = betsRef.current.length
      ? api.post<SpinResponse>('/roulette/spin', {
          tableLabel: table?.id,
          bets: betsRef.current.map((b) => ({ kind: b.kind, numbers: b.numbers, amount: b.amount })),
        })
      : null;

    setPhase('closing');       // board still on screen, but shut
    setCall('shut');           // "Stop Betting", read where the player is looking
    after(CLOSE_MS, () => { setCall(null); void doSpin(); });
  }

  async function doSpin() {
    // The table spins whether or not the player bet — it is a timer, not a
    // request. Guarding on bets.length here would stall the whole round for
    // anyone who sat one out.
    //
    // TWO GUARDS, and the second is the one that matters. The phase check reads
    // `phase` from THIS RENDER'S CLOSURE -- by the time a timer calls in, that
    // value is whatever it was when the timer was armed, so a duplicate caller
    // armed during 'betting' sails through it. The ref is the real lock: taken
    // synchronously by the first caller, released when the next window opens,
    // so a second spin of the same round is structurally impossible no matter
    // how many timer chains survive. This is the line that makes double-settle
    // unreachable rather than merely unlikely.
    // The shared path never draws its own number — `randomNumber()` below is the per-user
    // topology's "nobody staked, just turn the wheel" case, and on a shared table the wheel
    // belongs to every player at it. See closeBetting's guard.
    if (shared) return;
    if (phase !== 'betting' && phase !== 'closing') return;
    if (spinLockRef.current) return;
    spinLockRef.current = true;

    /* THE SERVER DECIDES. The request left in closeBetting; by now the Stop-
       Betting beat has played and the answer is usually already here. A staked
       spin with no server answer NEVER animates a locally-drawn number — a
       'win' the wallet does not back is the false-green the engineering
       principles ban — it re-opens betting with the slip intact instead. An
       EMPTY spin draws locally: nothing was debited, the wheel just turns. */
    const req = spinReqRef.current;
    spinReqRef.current = null;
    let serverRes: SpinResponse | null = null;
    if (req) {
      try {
        serverRes = await req;
      } catch (e) {
        // Nothing was debited (the server refuses atomically). Say why, take
        // the authoritative balance, and hand the window back with the chips
        // exactly where the player left them.
        const msg = e instanceof Error && e.message ? e.message : 'Could not reach the table';
        flash(msg);
        sDenied();
        try {
          const st = await api.get<{ wallet: number }>('/roulette/state');
          setBalance(st.wallet); setWalletBalance(st.wallet);
        } catch { /* keep the local figure; the next settle corrects it */ }
        bettingRef.current = true;
        spinLockRef.current = false;
        windowStart.current = 0;
        setPhase('betting');
        setCall('open');
        return;
      }
    }
    const n = serverRes ? serverRes.n : randomNumber();
    const plan = planSpin(n, { quick });

    // From the REF, never the closure — see betsRef. This is the money.
    slipRef.current = betsRef.current.map((b) => ({ ...b }));
    // From the REF as well. Same stale-closure trap as slipRef: read from the
    // timer's closure this was always [], so Rebet silently replayed nothing.
    // Only when the player actually staked: the table spins on a timer whether
    // or not anyone bet, and unconditionally overwriting here meant sitting out
    // ONE spin erased the slip Rebet exists to repeat. (Audit m2.)
    if (betsRef.current.length) setLastBets(betsRef.current.map((b) => ({ ...b })));
    setOutcome(null); setWinner(null);
    setHls({});
    setPhase('arming');                     // board locks, the peephole opens
    setBanner(null);
    navigator.vibrate?.(14);
    sSpinStart();

    after(ARM_MS, () => {                   // ~420ms grow + ~1s hold, NOTHING rotating
      setSpin(plan); setStartedAt(performance.now()); setPhase('spinning');
      // The ball's voice: a rolling hiss that falls in pitch as it slows, the
      // rattle across the deflectors, then the pocket.
      const stopRoll = sBallRoll(plan.T * 0.82);
      rollRef.current = stopRoll;
      after(plan.T * 820, () => { stopRoll(); sBallDrop(); });

      after(plan.T * 1000 + 250, () => {
        /* ── t = 0 — the ball seats ── */
        setWinner(n); setPhase('result');
        // The room REMEMBERS this number: lobby card, rejoin seed and Record
        // all read back through the same overlay, so the table can never
        // contradict what the player just watched. (Audit m6/m13.)
        if (table) recordResult(table, n, Date.now());
        // Client settle stays for the VISUALS (winIds, zone wash, per-cell
        // highlights); the MONEY is the server's. The two disagreeing is the
        // parity alarm — log it loudly, and the server always wins.
        const s = settle(slipRef.current, n);
        const staked = serverRes ? serverRes.staked : s.staked;
        const returned = serverRes ? serverRes.returned : s.returned;
        if (serverRes && (s.returned !== serverRes.returned || s.staked !== serverRes.staked)) {
          console.error('[roulette] payout parity drift', { client: s, server: serverRes });
        }
        const won = returned > 0;
        if (serverRes) {
          // The authoritative wallet, written exactly at the settle beat (any
          // earlier and purse = balance − totalBet double-subtracts the stake
          // for the rest of the spin). The site chip follows.
          setBalance(serverRes.newBalance);
          setWalletBalance(serverRes.newBalance);
        }
        // Held back from the displayed wallet so the count-up has somewhere to
        // count FROM. Only for a win — a loss has nothing to pay in.
        setCreditPending(won ? returned : 0);
        setYouWin(returned);
        setOutcome({ net: returned - staked, returned });
        // The round, as the player will want to read it back later. Recorded
        // from the frozen slip, so it is exactly what was settled.
        //
        // Only rounds the player actually BET in. The table spins every 20s
        // whether or not anyone staked, and listing those would fill "My Bets"
        // with rows reading "0 bets, staked 0, +0" — the owner asked for "all
        // its betting that it has done", and a spin you sat out is not one.
        if (staked > 0) {
          setLedger((L) => [{ n, staked, returned, bets: slipRef.current.length }, ...L].slice(0, 60));
        }
        setHistory((h) => [n, ...h].slice(0, 24));
        setHls({ winner: { reason: 'winner', cells: cellsForNumbers([n]), marker: null, seq: ++seqRef.current } });
        navigator.vibrate?.([8, 40, 8]);
        sSettle();

        /* ── t = 1200 … t = 4790 — the six presentation beats ──
             EXTRACTED, not changed (2026-09-06): the shared-table path has to play the exact
             same sequence off the server's `settles_at`, and two copies of a six-beat timeline
             is two things to keep in step. `resultBeats` is those six `after()` calls and
             nothing else — the money, the Record and the clear-down stayed here, because those
             are the parts that genuinely differ between a per-user spin and a shared round. */
        resultBeats({ n, won, bannerAmount: won ? s.returned : s.staked, returned: s.returned });

        /* ── t = 7000 — clear down and arm the next round ── */
        after(ROUND_END_MS, () => {
          // bettingRef is re-armed ONLY here, in the same block that atomically
          // clears the bets and re-enters 'betting'. Anywhere else and a bet
          // could land against a settled slip.
          bettingRef.current = true;
          spinLockRef.current = false;   // the round is over; the next one may spin
          windowStart.current = 0;      // the next window sets it, after its call clears
          setPhase('betting'); setBets([]); setJournal([]);
          setSpin(null); setStartedAt(null); setHls({}); setBanner(null);
          setZonesLit(false); setCreditPending(0);
          // The winner dies WITH its round. Left standing, chipsOnFelt marked
          // every fresh next-round chip that happened to cover last round's
          // number as already-won -- a new red/black chip played the win pulse
          // before the wheel had spun. (Audit m5.)
          setWinner(null);
          // The standing CARRIES OVER — that is what makes it a standing. Only
          // the round's gains clear, and roughly one seat in eight is swapped
          // out so the table does not become a fixed cast.
          setGains({});
          setYouWin(0);
          setSeats((prev) => rotateRoster(prev, Math.random));
          setTableTarget(pickTableTarget(Math.random));
        });
      });
    });
  }

  /* ═══ SHARED MODE — the server drives the round (Phase E, plan §5.7, 2026-09-06) ══════
   *
   * Everything above this comment is the per-user path and is exactly what shipped on
   * 1 September. Everything below runs ONLY when `shared` is non-null.
   *
   * THE SHAPE. `closeBetting`/`doSpin` are a chain of `setTimeout`s that OWN the round: they
   * decide when betting shuts, ask the server for a number, and then play the animation. Here
   * the round is a fact the server publishes and this effect is a projection of it — it reads
   * the clock on every emit (and on a 100 ms tick inside the clock) and moves the Page's
   * `phase`, `secs`, `call` and `winner` to whatever the stamps say they should be. There is
   * no local timer that can decide anything, which is the point: a table with two clocks
   * eventually closes on the wrong one, and closing on the wrong one is a bet that lands after
   * the window.
   *
   * WHAT IS *NOT* A PROJECTION. Two things have to fire ONCE per round rather than be
   * re-derived, because they are events and not states:
   *   • THE SEAT. The ball settling is the trigger for a seven-second sequence of sounds,
   *     coins and a count-up, and re-deriving it every 100 ms would restart the sequence
   *     sixty times. `seatedRef` fires it once per round number.
   *   • THE CLEAR-DOWN. The felt is emptied and the next round's carry-over chips are laid out
   *     when the server opens a NEW round number, not on a timer.
   * Everything else — which phase, how many seconds, whether the board takes chips — is a pure
   * function of (stamps, now) and is recomputed freely.
   */
  const seatedRef = useRef<number | null>(null);
  const shownRoundRef = useRef<number | null>(null);
  const spinPlanRef = useRef<{ no: number; plan: Spin; startedAt: number } | null>(null);
  /** The clock's rejection counter as of the last reconcile — see the block below. */
  const rejectSeenRef = useRef(0);
  /** The round whose winnings have already been held back for the count-up. */
  const creditHeldRef = useRef<number | null>(null);

  useEffect(() => {
    if (!shared) return;
    let dead = false;

    const sync = () => {
      if (dead) return;
      const r = shared.round();
      if (!r) return;

      /* ── the wallet, the bounds, the record ─────────────────────────────
         All three are the server's, re-read on every emit because an ack can move the wallet
         between polls. `walletLoaded` is the same gate the local path uses; it opens on the
         clock's first `/state` (or its first ack) and never closes. */
      if (shared.walletReady()) {
        walletLoaded.current = true;
        setWalletReady(true);
        setBalance(shared.balance());
      }
      const lim = shared.limits();
      if (lim) {
        // `maxPerSpin` has no equivalent on the shared wire (the service's per-round cap is
        // `max_bet_per_user_round`, which roulette's config does not set). 0 means "no cap"
        // here exactly as it does in `model.placeBet`, and the per-position `max` still bites.
        srvBet.current = { min: lim.min, max: lim.max, maxPerSpin: lim.maxPerRound || 0 };
        setSrvMin(lim.min);
      }
      const h = shared.history();
      setHistory((prev) => (prev.length === h.length && prev[0] === h[0] ? prev : h.slice(0, 24)));

      /* ── the phase ──────────────────────────────────────────────────────
         `shared.phase()` is `sharedTiming.phaseAt` over the server's stamps: betting until the
         lock cushion, closing through the arm, arming/spinning across `resolving`, then the
         result. It STALLS in `closing` while the number is missing — see rule 1. */
      const ph = shared.phase();
      const n = shared.winner();
      setSecs(shared.secs());
      // The money boundary, and it is the same ref the local path uses so `place()`, `undo()`
      // and every gesture guard are unchanged.
      bettingRef.current = ph === 'betting';

      /* ── the call banner ──
         'open' for the first INTRO_MS of a window (so "Place Your Bet" still lands), then out
         of the way; 'shut' for the whole of `closing`. */
      if (ph === 'betting') {
        const openFor = performance.now() - r.opensAt;
        setCall(openFor < INTRO_MS ? 'open' : null);
      } else if (ph === 'closing') {
        setCall('shut');
      } else {
        setCall(null);
      }

      /* ── a NEW round: clear the felt, then lay out anything the lock refused ── */
      if (shownRoundRef.current !== r.no) {
        const first = shownRoundRef.current == null;
        shownRoundRef.current = r.no;
        creditHeldRef.current = null;
        spinLockRef.current = false;
        spinPlanRef.current = null;
        setSpin(null); setStartedAt(null);
        setWinner(null); setOutcome(null); setBanner(null);
        setZonesLit(false); setCreditPending(0); setHls({});
        setBets([]); setJournal([]);
        setYouWin(0); setGains({});
        if (!first) {
          setSeats((prev) => rotateRoster(prev, Math.random));
          setTableTarget(pickTableTarget(Math.random));
        }
        /* §5.7: "those chips pre-fill the next round's slip".
           KEPT FOR NEXT SPIN is a PROMISE, so it is kept: each condemned chip is placed again,
           as a brand-new placement with a brand-new idempotency key, the instant the next
           window opens. The player was told at the lock that this would happen, they can see
           the chips land, and the whole window is open to Undo or Clear them — which is what
           makes this different from the server quietly sliding a stake forward (the thing
           `supportsAllowNext: false` refuses to do).
           A chip they can no longer afford is refused by `addChip`'s own balance guard and
           says so, rather than being placed on credit. */
        const carry = shared.takeCarryOver();
        if (carry.length) {
          // Out of this render's commit: `addChip` writes `bets`/`journal`, which the
          // clear-down above has just emptied in the same pass.
          setTimeout(() => {
            if (dead) return;
            let back = 0;
            for (const c of carry) {
              const last = lastPlacedRef.current.get(c.posId);
              if (addChip({
                id: c.posId, kind: c.kind, numbers: c.numbers,
                title: last?.title ?? c.posId, sub: last?.sub ?? '',
                fx: last?.fx ?? null, fy: last?.fy ?? null, centre: last?.centre ?? false,
              }, c.amount)) back += c.amount;
            }
            if (back > 0) flash(`Kept from last spin — ₹${back.toLocaleString('en-IN')} placed`);
          }, 0);
        }
      }

      /* ── the spin: one plan per round, aimed at the SERVER's pocket ──
         Built the first time the phase reaches `arming` WITH a number in hand. `startedAt` is
         `resolves_at + ARM_MS` in the perf domain — a time that may already be in the past if
         the player joined mid-spin or came back from a background tab, which is exactly right:
         `Wheel.tsx` renders the ball at `(now − startedAt)/1000` and therefore catches up to
         wherever the table actually is instead of restarting the flight. */
      if ((ph === 'arming' || ph === 'spinning' || ph === 'result' || ph === 'reveal')
          && n != null && r.resolvesAt != null && spinPlanRef.current?.no !== r.no) {
        const settlesAt = r.settlesAt ?? (r.resolvesAt + 7000);
        const seconds = flightSecondsFor(r.resolvesAt, settlesAt);
        // The ROTOR's starting angle is derived from the round id so every device at this
        // table has the wheel at the same rotation. The ball's own wobble stays per-device —
        // it is cosmetic, and the solver guarantees the landing whatever the wobble is.
        const rotorA0 = ((r.id % 3600) / 3600) * Math.PI * 2;
        const plan = planSpin(n, { seconds, rotorA0 });
        const startedAt = r.resolvesAt + 1400;   // sharedTiming.ARM_MS
        spinPlanRef.current = { no: r.no, plan, startedAt };
        setSpin(plan); setStartedAt(startedAt);
      }

      /* ── THE FELT, RECONCILED — only when a chip has actually been refused ───────────
         `bets` is drawn from the taps; the clock knows which of those taps the table really
         took. They can disagree in exactly one direction — rupees that came back — and that
         happens at exactly two moments: a `/bet` refused mid-window, and the lock condemning
         everything un-acked. `shared.rejections()` counts both, so the felt is rewritten when
         one happens and at no other time.
         NOT on every tick, which would be a race rather than a safeguard: `addChip` posts to
         the clock and writes its own `bets` row in one synchronous call, so a reconcile landing
         between the two would set the row to a total the `+=` updater is about to add to again
         — a chip drawn at twice its stake. (The clock's `place()` defers its emit for the same
         reason, so the two guards are belt and braces.)
         What it fixes: a position holding ₹100 acked and ₹200 refused must print ₹100, because
         the ₹200 is already back in the wallet. `chipOf` decides both the face and the paint. */
      const rej = shared.rejections();
      if (rej !== rejectSeenRef.current) {
        rejectSeenRef.current = rej;
        setBets((prev) => {
          let changed = false;
          const next: Placed[] = [];
          for (const b of prev) {
            const c = shared.chipOf(b.id);
            if (!c) { changed = true; continue; }        // nothing left on this square
            if (c.amount === b.amount) { next.push(b); continue; }
            changed = true;
            next.push({ ...b, amount: c.amount });
          }
          return changed ? next : prev;
        });
      }

      setPhase((prev) => (prev === ph ? prev : ph as Phase));
      if (ph === 'arming' && spinPlanRef.current?.no === r.no && !spinLockRef.current) {
        spinLockRef.current = true;
        navigator.vibrate?.(14);
        sSpinStart();
        /* The ball's voice, anchored to the SERVER's launch instant rather than to the moment
           this client noticed the arm. A poll can be up to ~400 ms late, and a rolling hiss
           that starts 400 ms after the ball does is audibly out of step with a wheel every
           other device at the table is drawing from the same stamps. `startedAt` is
           `resolves_at + ARM_MS` in the perf domain, so this is simply "how long until the
           ball actually launches", clamped at zero for a late join. */
        const T = spinPlanRef.current.plan.T;
        const launchIn = Math.max(0, spinPlanRef.current.startedAt - performance.now());
        after(launchIn, () => {
          if (dead) return;
          const stop = sBallRoll(T * 0.82);
          rollRef.current = stop;
          after(T * 820, () => { stop(); sBallDrop(); });
        });
      }

      /* ── THE SEAT — once per round, on the server's number ── */
      if ((ph === 'result' || ph === 'reveal') && n != null && seatedRef.current !== r.no) {
        seatedRef.current = r.no;
        /* REBET repeats what was actually AT RISK, so a chip the lock refused is not in it —
           it has already been carried into this round and re-placed, and repeating it as well
           would double it. Only rounds the player really staked overwrite the memory, exactly
           as on the local path (audit m2). */
        {
          const risked = betsRef.current.filter((b) => shared.ackOf(b.id) === 'placed');
          if (risked.length) setLastBets(risked.map((b) => ({ ...b })));
        }
        setWinner(n);
        setHls({ winner: { reason: 'winner', cells: cellsForNumbers([n]), marker: null, seq: ++seqRef.current } });
        navigator.vibrate?.([8, 40, 8]);
        sSettle();
        const o = shared.outcome(returnOf);
        const staked = o?.staked ?? 0;
        const returned = o?.returned ?? 0;
        const won = returned > 0;
        setYouWin(returned);
        setOutcome(o ? { net: o.net, returned } : null);
        /* NOT `setCreditPending(returned)` HERE — and this is the one place the two topologies
           genuinely cannot share a line. On the per-user path the settle is the SAME response
           that hands back `newBalance`, so at the seat the wallet already contains the win and
           holding it back is what gives the count-up something to count from. On a shared
           table the worker settles the whole table a beat later, so at the seat the balance is
           post-stake and PRE-win: subtracting the win here would show the player less money
           than they actually have, for seconds, right after they won. It is held back below
           instead — the moment the ledger row lands, which is the moment the money is real. */
        setCreditPending(0);
        if (staked > 0) {
          setLedger((L) => [{ n, staked, returned, bets: shared.placements().filter((p) => p.state === 'placed').length }, ...L].slice(0, 60));
        }
        /* A LATE JOINER DOES NOT GET THE FIREWORKS.
           `resultBeats` is a seven-second sequence measured from the seat. Fire it for someone
           who walked in three seconds after the ball landed and it runs past `next_opens_at`,
           so the wheel is announcing the last number over the top of the next betting window.
           Inside a quarter-second of the seat it is the real thing; past that, the result is
           simply SHOWN — the number, the highlight and the outcome are all already set above,
           which is everything that is true. Nothing about the money differs. */
        const seatedFor = r.settlesAt == null ? 0 : performance.now() - r.settlesAt;
        if (seatedFor <= 400) {
          resultBeats({ n, won, bannerAmount: won ? returned : staked, returned });
        } else {
          setCreditPending(0);
          setBanner({ won, amount: won ? returned : staked });
        }
      }

      /* The ledger row lands a beat after the seat (the worker settles the table
         asynchronously). When it does, it REPLACES the provisional figure — the server's
         `shared_user_ledger` is the money and the derived one was only ever a stand-in for the
         second it takes to arrive. */
      if ((ph === 'result' || ph === 'reveal') && n != null && seatedRef.current === r.no) {
        const o = shared.outcome(returnOf);
        if (o) {
          setOutcome((prev) => (prev && prev.returned === o.returned && prev.net === o.net
            ? prev : { net: o.net, returned: o.returned }));
          setYouWin((prev) => (prev === o.returned ? prev : o.returned));
          /* THE CREDIT LANDS. Hold it out of the displayed wallet exactly once, so the count-up
             at t = 4790 has somewhere to count FROM — but only if the ledger arrived in time to
             be counted. A settle that lands after that beat simply shows up as a higher
             balance, which is the truth arriving late rather than an animation replaying. */
          const seatAt = r.settlesAt;
          if (o.returned > 0 && creditHeldRef.current !== r.no
              && seatAt != null && performance.now() < seatAt + COUNT_UP_MS) {
            creditHeldRef.current = r.no;
            setCreditPending(o.returned);
          }
        }
      }
    };

    const un = shared.subscribe(sync);
    sync();
    return () => { dead = true; un(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared]);

  /* ─── MY BETS, in shared mode: the §5.7 UNION ────────────────────────────────────────
     §5.7 asks for one continuous history across the flip — `roulette_rounds` for every spin
     the player took on the per-user path and `shared_user_ledger` for every round since, in
     `settled_at` order — and `model.getHistoryFor` serves exactly that at `GET
     /roulette/history` in BOTH flag states.
     It is read WHEN THE SHEET OPENS, not polled: it is a receipt, nobody is watching it
     change, and the session ledger the local path keeps is not the same thing (it is this
     session only, and it is the thing the flip makes incomplete). The session ledger is left
     exactly as it is for the local path — it is what has shipped since 1 September and it is
     correct there, because a per-user spin's history IS the session plus `roulette_rounds`. */
  const [sharedLedger, setSharedLedger] = useState<{ n: number; staked: number; returned: number; bets: number }[]>([]);
  useEffect(() => {
    if (!shared || !sheetMyBets) return;
    let dead = false;
    shared.fetchHistory().then((rows: HistoryRow[]) => {
      if (dead) return;
      setSharedLedger(rows
        // A shared round whose result JSON would not parse has no number to show. It is
        // dropped rather than rendered as a blank square — the money is still in the ledger
        // and the totals below the list are the server's either way.
        .filter((r) => r.n != null)
        .map((r) => ({ n: Number(r.n), staked: r.staked, returned: r.returned, bets: r.bets })));
    }).catch(() => { /* the sheet shows what it already had */ });
    return () => { dead = true; };
  }, [shared, sheetMyBets]);

  /* SHARED MODE: the table's maintenance switch. One toast, once, if the admin has the game
     off — the same message the local boot shows, from the same config key. */
  useEffect(() => {
    if (!shared) return;
    if (shared.enabled()) return;
    flash('Roulette is unavailable right now.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared, walletReady]);

  /* ─── the readout ───────────────────────────────────────────────────── */

  const previewBet = preview;
  /* SHARED MODE: the rupees on this round that never reached the table. Zero on the local
     path (there is nothing to be un-acked — the slip is posted once, atomically, at the
     close), so every line below that reads it is inert there. (Phase E, §5.7.) */
  const notPlaced = shared ? shared.unplacedTotal() : 0;
  const status = (() => {
    /* §5.7's own words, and they go IN FRONT of the round's own status line: a player whose
       chip did not make it needs to know before they read anything else, and they need to
       know that it has not been silently eaten. It outranks NO MORE BETS because it is the
       one message on this screen that is about the player's own money rather than the
       table's state. */
    /* THE STALL, MADE VISIBLE. `phaseAt` holds the table in `closing` for as long as the
       server has not published the number (it will not invent one), and past a second that is
       worth saying out loud rather than leaving the player watching a still wheel. */
    if (shared && phase === 'closing' && shared.syncing()) {
      return {
        title: 'SYNCING WITH THE TABLE',
        sub: 'The wheel spins as soon as the table answers — your bets are safe',
        tone: 'locked',
      } as const;
    }
    if (notPlaced > 0 && (phase === 'closing' || phase === 'arming' || phase === 'spinning')) {
      return {
        title: 'NOT PLACED — KEPT FOR NEXT SPIN',
        sub: `₹${notPlaced.toLocaleString('en-IN')} did not reach the table in time — it goes on the next spin`,
        tone: 'bad',
      } as const;
    }
    if ((phase === 'result' || phase === 'reveal') && winner != null) {
      const c = colourOf(winner).toUpperCase();
      return {
        title: `${winner} ${c}`,
        sub: outcome && outcome.returned > 0
          ? `You won ₹${outcome.returned.toLocaleString('en-IN')}`
          : 'No win this round',
        tone: outcome && outcome.returned > 0 ? 'win' : 'flat',
      } as const;
    }
    if (inRound) return { title: 'NO MORE BETS', sub: 'Round in progress', tone: 'locked' } as const;
    if (previewBet) {
      const affordable = chip <= purse;
      return {
        title: previewBet.title,
        sub: affordable
          ? `₹${chip.toLocaleString('en-IN')} → ₹${returnOf(previewBet.kind, chip).toLocaleString('en-IN')} · ${previewBet.sub}`
          : 'Not enough balance — pick a smaller chip',
        tone: affordable ? 'flat' : 'bad',
      } as const;
    }
    // Two hints, alternating by state, so each one fits on one line at 390px
    // and between them they teach all three ways to bet.
    /* The headline is MONEY AT RISK. In shared mode a refused chip stays on the felt greyed
       (§5.7), and its rupees are already back in the wallet — counting them in "Total bet"
       would tell the player they have more on the table than they do. */
    if (bets.length) return { title: `Total bet ₹${Math.max(0, totalBet - notPlaced).toLocaleString('en-IN')}`, sub: 'Hold any number to see every bet there', tone: 'flat' } as const;
    return { title: 'Place your bets', sub: 'Tap a number · drag a chip to a line or corner', tone: 'flat' } as const;
  })();

  const chipsOnFelt: PlacedChip[] = bets
    .filter((b) => b.fx != null && b.fy != null)
    .map((b) => ({
      id: b.id, amount: b.amount, fx: b.fx!, fy: b.fy!, centre: b.centre,
      /* A chip that never reached the table cannot WIN, however the wheel lands — it was not
         in the round. Marking it won would pulse it gold, fly a coin off it and put it in the
         count-up, for money the player is not owed. */
      won: winner != null && b.numbers.includes(winner) && (!shared || shared.ackOf(b.id) === 'placed'),
      /* TRANSLUCENT UNTIL ITS ACK, SOLID AFTER IT (§5.7). Undefined on the local path, where
         the attribute is simply absent and the chip paints exactly as it always has — the
         per-user slip is not posted until the close, so "acked" has no meaning there. */
      ack: shared ? shared.ackOf(b.id) : undefined,
    }));

  return (
    <div
      ref={rootRef}
      className="ludo-root roulette-root fixed inset-0 z-[9999]"
      data-round={inRound ? '1' : '0'} data-phase={phase}
      data-cam={camUp ? 'up' : 'rest'} data-cam-moving={camMoving ? '1' : '0'}
      // Every tap ENDS here, whatever it started on. Hanging pointerup on each
      // surface stranded gestures the moment a finger lifted over an element
      // without a handler, and the felt went permanently dead.
      onPointerMove={onBoardMove} onPointerUp={onBoardUp}
      onPointerCancel={onBoardCancel} onLostPointerCapture={onBoardCancel}
    >
      <div ref={appRef} className={`app-container rlt-app ${peek ? 'rlt-peeking' : ''}`}>

        {/* ═══ PINNED CHROME ═══════════════════════════════════════════════
             Sibling of the stage, NOT a child. A transformed ancestor becomes
             the containing block for its fixed descendants, so anything nested
             inside .rlt-stage rides the pan no matter how it is positioned.
             This is where the reference's three green buttons will live. ═══ */}
        <div ref={chromeRef} className="rlt-chrome">

        {/* ── THE WALLET, top right ────────────────────────────────────
             Owner's call: the balance moves out of the bottom bar and up to
             where the three buttons used to be, Ludo-style, with no top bar
             around it. It is chrome, so it costs the board no height at all —
             and that height is the whole point of this pass. ── */}
        {/* THE QUICK BUTTON IS GONE. It toggled the spin's pace, and the owner
            moved that decision out to the lobby: "they should be sessions where
            the user gets to know that this particular session is a quick one...
            when the user gets inside it should not be able to switch off the
            quick session — it should be based on the session that it has
            selected." So `quick` below is read from the room, once, and there is
            nothing in here that can change it. */}

        {/* THE TABLE ID — "slightly highlighted, not too much space" (owner).
            Two players sitting together can see at a glance that they are at two
            different tables, which is the point of the whole lobby. */}
        {table && (
          <div className="rlt-tableid" aria-label={`Table ${table.id}`}>
            <span className="rlt-tableid-name">{table.name}</span>
            <span className="rlt-tableid-n">#{table.id}</span>
          </div>
        )}

        {/* THE TOP ROW: the player's money, then the way into everything else.
            The menu used to be a button down the right rail; the owner moved it
            "right after the user's wallet amount in the same row", and that is
            the right corner for it — the two things a player reaches for that
            are not a bet are their balance and the menu, and the thumb goes
            there already. */}
        <div className="rlt-topright">
          <div className="rlt-wallet">
            {/* A BALANCE NOBODY HAS CONFIRMED IS NOT A BALANCE (2026-09-06).
                This pill used to read a confident `0` for the length of the boot fetch —
                real rupees, on a public game, shown before the server had said a word. Every
                other game in this family shows an em dash until its first `/state` lands
                (andar_bahar/Page.tsx `walletReady()`); this now does the same, in BOTH
                topologies, because the argument has nothing to do with which one is running.
                The count-up writes through this same ref, and it can only run after a settle,
                which can only happen once the wallet is known. */}
            <span ref={walletRef} className="rlt-wallet-v"
                  title={walletReady ? 'Your balance' : 'Loading your balance'}>
              {walletReady ? purse.toLocaleString('en-IN') : '\u2014'}
            </span>
            {/* Opens the PRACTICE-CHIPS card, not /wallet. Two reasons, both
                from the 2026-08-16 audit (C11): navigating ejected the player
                MID-ROUND with bets on the felt and no confirmation — and worse,
                it was a lie, because this standalone build plays with practice
                chips a real deposit can never reach. When the game is wired to
                the real wallet, this handler is the ONE line that changes. */}
            <button type="button" {...press(() => { sTap(); setTopup(true); })}
                    aria-label="Add chips" className="rlt-wallet-add">+</button>
          </div>
          <button type="button" {...press(() => { sTap(); setSettingsOpen((v) => !v); })}
                  aria-label="Menu" className="rlt-menubtn">
            <svg viewBox="0 0 24 24" aria-hidden>
              <rect x="3" y="5" width="18" height="2.6" rx="1.3" />
              <rect x="3" y="10.7" width="18" height="2.6" rx="1.3" />
              <rect x="3" y="16.4" width="18" height="2.6" rx="1.3" />
            </svg>
          </button>
        </div>

        {/* ── THE RIGHT RAIL ────────────────────────────────────────────
             My Bets, How to play, Settings, then the three money-clarity
             controls — stacked vertically down the right, under the results
             column. That column of felt was empty, and moving them here is what
             buys the bottom bar's height for the board. Pinned, not on the
             stage: a control that slides off during the spin is a control the
             player cannot trust. ── */}
        {/* THE RESULTS COLUMN IS PINNED, not table furniture.
            It used to ride the camera with the clock and the seats, which was
            the reference's behaviour — but the reference had nothing underneath
            it. Ours has the button dock, which is pinned, so on every pan the
            results slid 206 px down and landed on top of the first button. One
            column, one behaviour: both halves stay put. */}
        <RecordRail history={history} colourOf={colourOf}
                    onMyBets={() => setSheetMyBets(true)} />

        {/* WHAT IS LEFT ON THE RIGHT RAIL: undo, clear, rebet. Nothing else.
            My Bets and How-to-play became tiles in the menu, and the menu button
            itself has gone up to the wallet row — so the column that once held
            six controls holds the three that change money on the felt, which are
            the only three that have to be one tap away mid-round. */}
        <div className="rlt-siderail">
          <div className="rlt-tinyacts">
            {([['undo', undo, 'Undo'],
               ['delete_sweep', clear, 'Clear'],
               ['replay', rebet, 'Rebet'],
               /* PAYS — a cash glyph, not the old "×": the owner is right that
                  a cross reads as CLOSE. It also dresses louder than the three
                  chip actions (gold face, .rlt-tinyact--pay) because it is the
                  one button that answers a question instead of moving money —
                  the more players tap it, the fewer bet blind. */
               ['payments', () => { sTap(); setPeek(true); }, 'Pays']] as const).map(([icon, fn, label]) => (
              /* The label is part of the control, not a tooltip: these three are
                 the only buttons that take money off the felt, and an unlabelled
                 icon is a memory test at the worst moment. */
              <div key={label} className={`rlt-tinyact ${label === 'Pays' ? 'rlt-tinyact--pay' : ''}`}>
                <button type="button" disabled={!betting} aria-label={label}
                  {...press(fn)} className="rlt-tiny">
                  <Icon name={icon} size={13} />
                </button>
                <span className="rlt-tinyact-label">{label}</span>
              </div>
            ))}
          </div>
        </div>

          {/* SIMULATED. The countdown and the call banner are pinned chrome —
              the reference hides both outright while the camera is up. */}
          <CallBanner call={call} />

          {/* THE LAST FIVE SECONDS. 5 - 4 - 3 - 2 - 1, one per second, in the
              middle of the table, and then "Stop Betting" takes over. KEYED BY
              THE NUMBER so React remounts it each second and the entrance plays
              again; re-rendering the same node would just swap the glyph. */}
          {betting && secs >= 1 && secs <= 5 && (
            <CountIn key={secs} n={secs} onPip={sCountPip} />
          )}
        </div>{/* ═══ end pinned chrome ═══ */}

        {/* ═══ THE STAGE ═══════════════════════════════════════════════════
             One tall column, moved as a single composited transform between the
             two camera positions. Everything in here rides the pan. ═══ */}
        <div className="rlt-stage">

        {/* ── the wheel bay. Full size, ALWAYS — there is no window onto it any
             more. At rest it simply hangs above the canvas and the screen edge
             does the cropping, which is what the reference does. ── */}
        <div className="rlt-wheelbay">
          <Wheel spin={spin} startedAt={startedAt} idleFrom={idleFrom}
                 size={wheelPx} winner={winner} revealed={phase === 'result' || phase === 'reveal'}
                 ballVisible={spin != null && startedAt != null} />
        </div>

        {/* ── the felt. The WHOLE board, always. Nothing scrolls. ── */}
        {/* padding lives in CSS (--rlt-gutter), not in utility classes: it has
            to stay in lockstep with the table-edge overlay's width. */}
        <div ref={feltRef} className="rlt-felt overflow-hidden">
          <div className="rlt-feltinner"
               onPointerDown={onBoardDown} onPointerMove={onBoardMove}
               onPointerUp={onBoardUp} onPointerCancel={onBoardCancel}>
            <Board
              ref={gridRef}
              amountOf={amountOf} disabled={!betting}
              activeId={previewBet?.id ?? null} litCells={litCells}
              winner={phase === 'result' || phase === 'reveal' ? winner : null}
              winIds={winIds}
              onKeyPlace={onKeyPlace} focusCell={focusCell} setFocusCell={setFocusCell}
            >
              <Overlay lattice={lattice} highlight={highlight} chips={chipsOnFelt} dimOthers={inRound}
                       points={chip != null && betting && lattice ? linePoints(lattice) : null}
                       railL={railPx} />
              {/* SIMULATED — F4. A SIBLING of Overlay, never routed through its
                  `chips` prop: down that path these would be PlacedChip[] and
                  one .concat() away from merging with real money. */}
              {/* BOTH rails, not one. measureLattice takes them separately for the
                  same reason: the even-money rail is on the left and the dozen
                  rail on the right, and the fake layer has to know where each
                  one actually is or its chips land on the numbers instead. */}
              <FakeChipLayer active={betting} lattice={lattice}
                             railL={railPx} railR={railPx} />
              {/* THE PRICES. A child of Board for the same reason Overlay is:
                  it inherits the grid's own box, so every label is placed from
                  the lattice the taps are resolved against and cannot drift
                  from the square it is describing. */}
              {peek && (
                <PayoutPeek lattice={lattice} railL={railPx} railR={railPx}
                            onDone={() => setPeek(false)} />
              )}
            </Board>
          </div>
        </div>

        {/* ── THE SHELF ────────────────────────────────────────────────
             The table total, the player's own stake and the chips now share ONE
             band on the felt instead of a strip each with a black bar under
             them. That is ~7% of the screen's height handed back to the board,
             and it reads as one object rather than three stacked ones. ── */}
        {/* ── the chip tray, on its ornate gold rail ──────────────────────
             One row. The SPIN button is gone: the table is on a 20-second
             timer now and spins whether or not anyone bet, so a manual spin is
             a control with nothing left to do. Undo / Clear / Rebet stay —
             they are money-clarity controls, not decoration, and they cost
             nothing sitting on the Total Bet band above. ── */}
        <div className="rlt-trayrail">
          {/* The table's running total moved to the ALL PLAYERS tile, where the
              crowd it describes actually is. What is left here is the ONE number
              that has to be exact and has to be near the player's thumb: their
              own money on the felt. */}
          <div className="rlt-shelfline">
            <span className={`rlt-mystake ${totalBet > 0 ? 'on' : ''}`}>
              Your bet <b>₹{totalBet.toLocaleString('en-IN')}</b>
            </span>
          </div>
          <div ref={trayRef} className={`rlt-tray ${chip == null ? 'ask' : ''}`}>
            {CHIPS.map((c) => (
              <button key={c} type="button"
                disabled={!betting || c > purse || c < MIN_BET}
                onPointerDown={(e) => onTokenDown(e, c)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setChip(c); sChipPick(); } }}
                aria-label={c < MIN_BET ? `₹${c} chip — below this table’s minimum` : `₹${c} chip`}
                aria-pressed={chip === c}
                className={`rlt-chip ${chip === c ? 'sel' : ''} ${c > purse ? 'broke' : ''} ${c < MIN_BET ? 'under' : ''}`}>
                {c >= 1000 ? `${c / 1000}k` : c}
              </button>
            ))}
            {/* Say the rule where it binds: the tray is where a player finds
                out a chip is not accepted, so the tray is where the number
                belongs. Only shown when it actually excludes something. */}
            {MIN_BET > 10 && <span className="rlt-tray-min">min ₹{MIN_BET}</span>}
          </div>
        </div>

        {/* THE BOTTOM BAR IS GONE. Its four controls all found better homes —
             balance and "+" to the top right, settings to the right rail, and
             back into the settings menu, which already carried "Exit to Games".
             A whole band of chrome removed, and the board grew into it. */}

        </div>{/* ═══ end stage ═══ */}

        {/* ═══ THE TABLE FURNITURE ═════════════════════════════════════════
             SIMULATED, and a SIBLING of the stage carrying the SAME transform
             rather than a child of the felt.

             It has to ride the camera — spec §1 is explicit that the avatars
             and the Record rail move down with the table while the top buttons
             stay pinned — but it also has to paint ABOVE the table-edge overlay
             (.rlt-app::after, z-index 200). Inside the stage that is
             impossible: the stage is transformed, so it is its own stacking
             context and nothing within it can rise above a sibling at 200.

             That is not cosmetic. The reference puts the avatars at x=29/576
             and the Record rail hard against the right screen edge — both
             INSIDE the 14.93%-wide table edge — so as children of the felt all
             four were painted over and simply invisible.

             pointer-events:none throughout: they are decoration, and this also
             makes it structurally impossible for one to intercept a chip. ═══ */}
        <div className="rlt-furniture" aria-hidden>
          <Clock secs={secs} />
          <Leaderboard seats={seats} gains={gains}
                       running={betting} target={tableTarget} frozen={!betting}
                       table={table} windowMs={WINDOW}
                       you={youWin > 0 ? { ...youRef.current, win: youWin } : null} />
        </div>

        {/* The result inset — pinned chrome, so it does not ride the camera.
            It slides in as the pan starts and leaves before the next round. */}
        {winner != null && (phase === 'result' || phase === 'reveal') && (
          <ResultInset winner={winner} />
        )}

        {/* the table closing — costs zero layout height */}
        {(phase === 'arming' || phase === 'spinning') && <div className="rlt-stamp" aria-hidden>NO MORE BETS</div>}

        {toast && (
          <div className="rlt-toast" role="status">{toast}</div>
        )}

        {/* INSIDE .rlt-app, not beside it. The scrim is `position: absolute;
            inset: 0`, so it is bounded by the nearest positioned ancestor — and
            outside the canvas that is the whole viewport, which on anything
            wider than a phone puts the panel out on the page beside the game
            instead of in the table's own top-right corner. */}
        <RouletteMenu
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          soundOn={soundOn}
          voiceOn={voiceOn}
          musicOn={music.on}
          musicName={music.name}
          voiceNote={voiceInfo}
          onToggleSound={() => setSoundOn((v) => { setSoundEnabled(!v); if (!v) sTap(); return !v; })}
          /* Music is its own switch, not a sub-setting of Sounds. They are
             different things to want: plenty of players keep the chips and the
             ball and lose the bed, and a few do the opposite. */
          onToggleMusic={() => { sTap(); const r = cycleMusic(); setMusic({ on: r.on, name: r.track.name }); }}
          /* The Voice tile SPEAKS when tapped. That is deliberate: on iOS this is
             the only context speech can be unlocked from, so testing it and
             enabling it are the same action. The note it writes back says which
             voice was chosen, or why there is none. */
          /* A SWITCH THAT CONFIRMS ITSELF. Turning it on says "Voice on" in the
             voice the calls will use — it used to read out a fake result,
             "number twenty-one, red", which announced a number nobody had spun.
             Still fired from inside the tap, because that gesture is the only
             context iOS will unlock speech from. */
          onToggleVoice={() => setVoiceOnState((v) => {
            const next = setVoiceOn(!v);
            if (next) { unlockSpeech(); sVoiceOn(); } else { sTap(); }
            setVoiceInfo(speechStatus());
            return next;
          })}
          onRules={() => { setSettingsOpen(false); setSheetHelp(true); }}
          onMyBets={() => { setSettingsOpen(false); setSheetMyBets(true); }}
          onExitGame={() => { setSettingsOpen(false); navigate('/games'); }}
          onExit={() => {
            setSettingsOpen(false);
            // Back to the lobby if we came from one; otherwise out of the game
            // entirely, which is what this did before the lobby existed.
            if (onChangeTable) onChangeTable(); else navigate('/games');
          }}
          exitLabel={onChangeTable ? 'Change table' : 'Exit'}
        />
      </div>

      {/* the dragged chip. Drawn at the HIT POINT, 54px above the fingertip, so
          the chip sits ON the target and the finger covers nothing. */}
      {loupe && <Loupe x={loupe.x} y={loupe.y} target={loupe.target} chip={chip ?? 0} />}

      {banner && (
        <ResultCall won={banner.won} amount={banner.amount} />
      )}


      {sheetMyBets && (
        <MyBets rows={shared ? sharedLedger : ledger} onClose={() => setSheetMyBets(false)} />
      )}

      {topup && (
        <TopUp balance={balance}
               onAdd={() => { setTopup(false); navigate('/wallet'); }}
               onClose={() => setTopup(false)} />
      )}

      {sheetHelp && (
        <HowToPlay onClose={() => setSheetHelp(false)} windowSec={Math.round(WINDOW / 1000)} />
      )}

      {sheet && (
        <BetSheet targets={sheet} chip={chip} onClose={() => setSheet(null)}
                  onPick={(t) => { place(t, chip, t.marker); }} />
      )}
    </div>
  );
}

/**
 * THE LOUPE — shown only while a token is being carried, because that is the
 * only time a finger is sitting on top of the thing it is aiming at. It floats
 * above the fingertip with the 3x3 neighbourhood enlarged, the covered cells
 * ringed, and the return spelled out. The hit point itself stays honestly under
 * the finger; moving it (an earlier build put it 54px higher) is what made
 * aiming impossible.
 */
function Loupe({ x, y, target, chip }: { x: number; y: number; target: Target | null; chip: number }) {
  const CW = 46, CH = 30, G = 4, PAD = 8;
  const W = 3 * CW + 2 * G + PAD * 2;
  const H = 3 * CH + 2 * G + PAD * 2 + 26;
  /* Clamp in the APP'S OWN LANE, expressed in wrapper coordinates -- because
     that is the space the loupe's left/top live in (it is position:fixed
     inside the rotated wrapper). Two earlier versions each got one half
     wrong: window.innerWidth is the device's LONG edge sideways (clamp never
     fired), and a bare [0, appWidth] window assumed the app starts at
     wrapper x=0 -- but the app is a centered 430px column, so on any wider
     viewport (desktop carve-out, tablets) that clamp pinned the loupe ~343px
     while the board sat at ~720, detaching the magnifier from the cursor
     entirely. (Recheck P4.) The app's own edge, mapped through the SAME
     transform the pointer coordinates went through, is right in every case:
     phones (edge 0), desktop (edge (W-430)/2), rotated or not. Both corners
     are mapped and min() taken because under rotation the viewport's top-left
     corner is not the wrapper-local minimum. */
  const appEl = typeof document !== 'undefined'
    ? (document.querySelector('.rlt-app') as HTMLElement | null) : null;
  const vw = appEl ? appEl.offsetWidth : 390;
  let appLeft = 0;
  if (appEl) {
    const r = appEl.getBoundingClientRect();
    appLeft = Math.min(
      toWrapperLocal(r.left, r.top).x,
      toWrapperLocal(r.right, r.bottom).x,
    );
  }
  const left = Math.max(appLeft + W / 2 + 6, Math.min(appLeft + vw - W / 2 - 6, x));
  const above = y - H - 30;
  const top = above > 6 ? above : y + 46;

  const base = target?.cells[0] ?? { r: 1, c: 1 };
  const r0 = Math.max(0, Math.min(10, base.r - 1));
  const lit = new Set((target?.cells ?? []).map((c) => `${c.r}:${c.c}`));

  return (
    <div className="rlt-loupe" style={{ left, top, width: W, transform: 'translateX(-50%)' }} aria-hidden>
      <div className="rlt-loupe-grid" style={{ gridTemplateColumns: `repeat(3, ${CW}px)`, gap: G }}>
        {[0, 1, 2].map((dr) => {
          const r = r0 + dr;
          if (r === 0) {
            return (
              <div key="z" style={{ gridColumn: '1 / -1', height: CH }}
                   className={`rlt-lc green ${lit.has('0:0') ? 'lit' : ''}`}>0</div>
            );
          }
          return [0, 1, 2].map((c) => {
            const n = numAt(r, c);
            return (
              <div key={`${r}:${c}`} style={{ height: CH }}
                   className={`rlt-lc ${colourOf(n)} ${lit.has(`${r}:${c}`) ? 'lit' : ''}`}>{n}</div>
            );
          });
        })}
      </div>
      <div className="rlt-loupe-cap">
        {target
          ? <><b>{target.title}</b><span>₹{chip.toLocaleString('en-IN')} → ₹{returnOf(target.kind, chip).toLocaleString('en-IN')}</span></>
          : <b>Move onto the board</b>}
      </div>
    </div>
  );
}

/** Every bet that touches one cell, as 48px rows. This is how a corner or a six
 *  line becomes a real target instead of a 4mm point — and it doubles as the
 *  screen-reader path and as discovery for players who have never heard of a
 *  six line. One sheet, three jobs. */
/**
 * TopUp — the low-balance door to the REAL wallet.
 *
 * This used to mint practice chips; the game now plays on the signed wallet,
 * so the only honest button is the one that opens the deposit flow. It closes
 * the card first — the C11 audit's rule stands: never navigate a player away
 * with state they did not choose to leave.
 */
function TopUp({ balance, onAdd, onClose }: {
  balance: number; onAdd: () => void; onClose: () => void;
}) {
  return (
    <div className="rlt-sheet-wrap" {...press(onClose)} role="dialog" aria-label="Add money">
      <div className="rlt-sheet" onPointerDown={(e) => e.stopPropagation()}>
        <div className="rlt-sheet-title">
          {balance < 10 ? 'Out of chips' : 'Add money'}
        </div>
        <p className="rlt-topup-note">
          {balance < 10
            ? 'Your balance is too low to place a bet. Add money to keep playing.'
            : 'Top up your wallet — you will come back to this table.'}
        </p>
        <button type="button" className="rlt-topup-take" {...press(onAdd)}>
          Add money
        </button>
        <button type="button" className="rlt-topup-later" {...press(onClose)}>
          Not now
        </button>
      </div>
    </div>
  );
}

function BetSheet({
  targets, chip, onClose, onPick,
}: { targets: Target[]; chip: number; onClose: () => void; onPick: (t: Target) => void }) {
  return (
    <div className="rlt-sheet-wrap" {...press(onClose)} role="dialog" aria-label="All bets here">
      <div className="rlt-sheet" onPointerDown={(e) => e.stopPropagation()}
           onPointerUp={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-3 py-2">
          <div className="text-[13px] font-black text-amber-100 leading-tight">All bets here</div>
          <button type="button" {...press(onClose)} aria-label="Close"
            className="w-9 h-9 grid place-items-center rounded-full bg-black/40 text-emerald-100">
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="max-h-[46dvh] overflow-y-auto pb-2">
          {targets.map((t) => (
            <button key={t.id} type="button"
              {...press(() => { onPick(t); onClose(); })}
              className="w-full h-12 px-3 flex items-center justify-between gap-2 active:bg-white/10">
              <span className="min-w-0 text-left">
                <span className="block text-[13px] font-black text-white leading-tight truncate">{t.title}</span>
                <span className="block text-[10.5px] text-emerald-200/70 leading-tight truncate">{t.sub}</span>
              </span>
              <span className="shrink-0 text-[12.5px] font-black text-amber-200 tabular-nums leading-tight">
                ₹{chip.toLocaleString('en-IN')} → ₹{returnOf(t.kind, chip).toLocaleString('en-IN')}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * MY BETS — every round this session, and what it did to the wallet.
 *
 * The owner asked for exactly this in image 6: "a button where user could see
 * all its betting that it has done and whether it has won or not, how much it
 * has won or lost". So the columns are the four things that answer that, and
 * nothing else: what came, how many bets, what went in, what came back.
 */
function MyBets({ rows, onClose }: {
  rows: { n: number; staked: number; returned: number; bets: number }[];
  onClose: () => void;
}) {
  const staked = rows.reduce((a, r) => a + r.staked, 0);
  const back = rows.reduce((a, r) => a + r.returned, 0);
  const net = back - staked;
  return (
    <div className="rlt-sheet-wrap" {...press(onClose)} role="dialog" aria-label="My bets">
      <div className="rlt-sheet" onPointerDown={(e) => e.stopPropagation()}>
        <div className="rlt-sheet-top">
          <span className="rlt-sheet-title">My Bets</span>
          <button type="button" {...press(onClose)} aria-label="Close" className="rlt-sheet-x">✕</button>
        </div>

        {/* The answer to "am I up or down", before any of the detail. */}
        <div className={`rlt-mb-sum ${net >= 0 ? 'up' : 'down'}`}>
          <span className="rlt-mb-sum-k">{rows.length} round{rows.length === 1 ? '' : 's'}</span>
          <span className="rlt-mb-sum-v">
            {net >= 0 ? '+' : '−'}₹{Math.abs(net).toLocaleString('en-IN')}
          </span>
        </div>

        {rows.length === 0 ? (
          <p className="rlt-mb-empty">No bets yet this session. Place a chip and the round will show up here.</p>
        ) : (
          <div className="rlt-mb-list">
            <div className="rlt-mb-row rlt-mb-head">
              <span>Result</span><span>Bets</span><span>Staked</span><span>Won / Lost</span>
            </div>
            {rows.map((r, i) => {
              const d = r.returned - r.staked;
              return (
                <div className="rlt-mb-row" key={i}>
                  <span className={`rlt-mb-n rlt-mb-n--${r.n === 0 ? 'zero' : colourOf(r.n)}`}>{r.n}</span>
                  <span className="rlt-mb-c">{r.bets}</span>
                  <span className="rlt-mb-c">₹{r.staked.toLocaleString('en-IN')}</span>
                  <span className={`rlt-mb-d ${d >= 0 ? 'up' : 'down'}`}>
                    {d >= 0 ? '+' : '−'}₹{Math.abs(d).toLocaleString('en-IN')}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** What every bet pays, in the plainest wording that is still true. */
function HowToPlay({ onClose, windowSec }: { onClose: () => void; windowSec: number }) {
  /* GET-BACK language throughout, matching every X on the felt: "win X30"
     means a winning ₹100 puts ₹3,000 in your wallet. "Pays 1 to 1" is
     bookmaker-speak nobody should need a dictionary for. Labels match the
     board's own new text (1–12, the ↑ line). */
  const rows: [string, string][] = [
    ['A single number', `win X${RETURN_X.straight} your bet`],
    ['Line between two numbers', `win X${RETURN_X.split}`],
    ['A row of three', `win X${RETURN_X.street}`],
    ['Corner of four numbers', `win X${RETURN_X.corner}`],
    ['Two rows (six numbers)', `win X${RETURN_X.sixline}`],
    ['1–12 / 13–24 / 25–36', `win X${RETURN_X.dozen}`],
    ['↑ a full line of numbers', `win X${RETURN_X.column}`],
    ['Red, Black, Odd, Even, 1–18, 19–36', `win X${RETURN_X.red}`],
  ];
  return (
    <div className="rlt-sheet-wrap" {...press(onClose)} role="dialog" aria-label="How to play">
      <div className="rlt-sheet" onPointerDown={(e) => e.stopPropagation()}>
        <div className="rlt-sheet-top">
          <span className="rlt-sheet-title">How to play</span>
          <button type="button" {...press(onClose)} aria-label="Close" className="rlt-sheet-x">✕</button>
        </div>
        <p className="rlt-help-p">
          {/* THIS room's pace, not a hardcoded 20 -- quick tables bet for 12
              seconds and the sheet was flatly wrong there. (Audit m10.) */}
          Pick a chip, then tap the board. You have {windowSec} seconds each round. Tap
          the dots on the lines to cover two, four or six numbers at once.
        </p>
        <div className="rlt-help-list">
          {rows.map(([k, v]) => (
            <div className="rlt-help-row" key={k}><span>{k}</span><span>{v}</span></div>
          ))}
        </div>
        <p className="rlt-help-p rlt-help-note">
          Zero belongs to nobody: it wins no red, black, odd, even, 1~18 or 19~36 bet.
        </p>
      </div>
    </div>
  );
}
